/* Seasonal sale sections (sections/ls-sale-*.liquid): phases, time lines, countdown, section menu,
   highlight rail, drawers, timed add-to-cart buttons, the product grid's filters/sort, the savings
   meter, and GSAP motion (only with html.lsl-motion: animations on, no reduced motion, not in the
   theme editor). Every label comes from data attributes written by the sections. */
(() => {
  if (window.LsSale) return;
  window.LsSale = true;

  const config = () => document.querySelector('[data-lsl-tz]')?.dataset || {};
  const root = document.documentElement;
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const motionOn = () => root.classList.contains('lsl-motion') && !reduced.matches;
  let G = null;
  let ST = null;
  const tzText = () => (config().lslTz || '+08:00').trim();
  const tzMinutes = () => {
    const m = tzText().match(/^([+-])(\d{1,2}):?(\d{2})?$/);
    if (!m) return 480;
    return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0));
  };
  const isoOffset = () => {
    const mins = tzMinutes();
    const a = Math.abs(mins);
    return (mins < 0 ? '-' : '+') + String(Math.floor(a / 60)).padStart(2, '0') + ':' + String(a % 60).padStart(2, '0');
  };
  const parse = (v) => {
    if (!v) return null;
    const s = v.trim().replace(' ', 'T');
    const t = Date.parse((s.length === 10 ? s + 'T00:00' : s) + isoOffset());
    return Number.isFinite(t) ? t : null;
  };

  const lang = document.documentElement.lang || undefined;
  const fmt = (ts, isEnd) => {
    let t = ts;
    const local = new Date(t + tzMinutes() * 60000);
    const midnight = local.getUTCHours() === 0 && local.getUTCMinutes() === 0;
    if (isEnd && midnight) t -= 60000;
    const d = new Date(t + tzMinutes() * 60000);
    const showYear = config().lslYear === 'true' || d.getUTCFullYear() !== new Date().getFullYear();
    const date = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', day: 'numeric', month: 'short', ...(showYear ? { year: 'numeric' } : {}) }).format(d);
    const time = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    const label = config().lslTzLabel || '';
    return `${date}, ${time}${label ? ' ' + label : ''}`;
  };
  const fmtDay = (ts, isEnd) => fmt(ts, isEnd).split(',')[0];

  const phaseOf = (el, now = Date.now()) => {
    const forced = config().lslPreview;
    if (forced) return forced;
    const a = parse(el.dataset.lslFrom);
    const b = parse(el.dataset.lslTo);
    if (a && now < a) return 'upcoming';
    if (b && now >= b) return 'ended';
    return 'live';
  };
  const windowOf = (node) => node.closest('[data-lsl-window]');

  /* Add to calendar: a two-item menu. A .ics download alone opens whatever owns .ics on the device
     (Outlook on most Windows PCs) and does nothing useful on Android phones that only have Google
     Calendar, so every calendar button opens this menu: Google Calendar (its prefilled "new event"
     page) or Apple / Outlook (.ics). cal.menu(button, { title, start, end (ms), details, url,
     alarm (minutes before), file }). */
  const cal = (() => {
    const z = (ms) => new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const tx = (v) => String(v || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (m) => '\\' + m);
    const endOf = (ev) => (ev.end && ev.end > ev.start ? ev.end : ev.start + 3600e3);
    const notes = (ev) => [ev.details, ev.url].filter(Boolean).join('\n\n');
    const ICON_G = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16.5" rx="3" fill="#fff" stroke="#DADCE0"/><path d="M3 9h18" stroke="#DADCE0"/><rect x="3" y="4.5" width="18" height="4.5" rx="2" fill="#4285F4"/><text x="12" y="18.6" text-anchor="middle" font-family="Arial,sans-serif" font-size="8.5" font-weight="700" fill="#4285F4">31</text></svg>';
    const ICON_F = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19.5h14"/></svg>';
    let pop = null;
    let owner = null;
    const style = () => {
      if (document.getElementById('lsl-cal-css')) return;
      const st = document.createElement('style');
      st.id = 'lsl-cal-css';
      st.textContent = '.lsl-cal{position:fixed;z-index:2147483600;min-width:236px;padding:6px;border-radius:16px;background:#fff;color:#141414;box-shadow:0 1px 2px rgba(0,0,0,.12),0 18px 44px -12px rgba(0,0,0,.5);font-family:inherit;font-size:14px;font-weight:500;line-height:1.2;animation:lsl-cal-in .18s cubic-bezier(.23,1,.32,1)}'
        + '@keyframes lsl-cal-in{from{opacity:0;transform:translateY(-4px) scale(.98)}}'
        + '.lsl-cal a,.lsl-cal button{display:flex;align-items:center;gap:12px;width:100%;min-height:44px;margin:0;padding:0 12px;border:0;border-radius:11px;background:none;color:inherit;font:inherit;letter-spacing:normal;text-transform:none;text-align:left;text-decoration:none;cursor:pointer}'
        + '.lsl-cal a:hover,.lsl-cal button:hover,.lsl-cal a:focus-visible,.lsl-cal button:focus-visible{background:#f0f0f0;outline:none}'
        + '.lsl-cal svg{flex:none;width:22px;height:22px}.lsl-cal small{margin-left:auto;padding-left:10px;font-size:12px;color:#6b6b6b}'
        + '@media (prefers-reduced-motion:reduce){.lsl-cal{animation:none}}';
      document.head.append(st);
    };
    const ics = (ev) => {
      const alarm = ev.alarm == null ? 10 : ev.alarm;
      const body = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Linsoul//Sale//EN', 'CALSCALE:GREGORIAN', 'BEGIN:VEVENT',
        'UID:' + z(ev.start) + '-' + Math.random().toString(36).slice(2) + '@' + location.hostname,
        'DTSTAMP:' + z(Date.now()), 'DTSTART:' + z(ev.start), 'DTEND:' + z(endOf(ev)),
        'SUMMARY:' + tx(ev.title), 'DESCRIPTION:' + tx(notes(ev)), 'URL:' + (ev.url || ''),
        'BEGIN:VALARM', 'ACTION:DISPLAY', 'TRIGGER:-PT' + alarm + 'M', 'DESCRIPTION:' + tx(ev.title), 'END:VALARM',
        'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([body], { type: 'text/calendar;charset=utf-8' }));
      a.download = (ev.file || 'calendar') + '.ics';
      document.body.append(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
    };
    const google = (ev) => 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + encodeURIComponent(ev.title || '')
      + '&dates=' + z(ev.start) + '/' + z(endOf(ev)) + '&details=' + encodeURIComponent(notes(ev));
    const close = (refocus) => {
      if (!pop) return;
      pop.remove();
      pop = null;
      removeEventListener('pointerdown', outside, true);
      removeEventListener('keydown', key, true);
      removeEventListener('scroll', shut, true);
      removeEventListener('resize', shut);
      if (owner) { owner.setAttribute('aria-expanded', 'false'); if (refocus) owner.focus(); owner = null; }
    };
    const shut = () => close(false);
    const outside = (e) => { if (pop && !pop.contains(e.target) && !owner.contains(e.target)) close(false); };
    const key = (e) => {
      if (!pop) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const list = [...pop.querySelectorAll('a,button')];
        const i = list.indexOf(document.activeElement);
        e.preventDefault();
        list[(i + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length].focus();
      }
    };
    const menu = (btn, ev) => {
      if (!ev || !ev.start) return;
      if (pop && owner === btn) { close(true); return; }
      close(false);
      style();
      pop = document.createElement('div');
      pop.className = 'lsl-cal';
      pop.setAttribute('role', 'menu');
      const g = document.createElement('a');
      g.setAttribute('role', 'menuitem');
      g.href = google(ev);
      g.target = '_blank';
      g.rel = 'noopener';
      g.innerHTML = ICON_G + '<span>Google Calendar</span>';
      const f = document.createElement('button');
      f.type = 'button';
      f.setAttribute('role', 'menuitem');
      f.innerHTML = ICON_F + '<span>Apple / Outlook</span><small>.ics</small>';
      g.addEventListener('click', () => close(false));
      f.addEventListener('click', () => { ics(ev); close(true); });
      pop.append(g, f);
      /* inside the planner dialog the menu has to live in the dialog, or it renders underneath it */
      (btn.closest('dialog') || document.body).append(pop);
      owner = btn;
      btn.setAttribute('aria-haspopup', 'menu');
      btn.setAttribute('aria-expanded', 'true');
      const r = btn.getBoundingClientRect();
      const w = pop.offsetWidth;
      const h = pop.offsetHeight;
      pop.style.left = Math.max(8, Math.min(r.left, innerWidth - w - 8)) + 'px';
      pop.style.top = (r.bottom + 8 + h <= innerHeight - 8 ? r.bottom + 8 : Math.max(8, r.top - h - 8)) + 'px';
      g.focus({ preventScroll: true });
      setTimeout(() => {
        addEventListener('pointerdown', outside, true);
        addEventListener('keydown', key, true);
        addEventListener('scroll', shut, { capture: true, passive: true });
        addEventListener('resize', shut);
      });
    };
    return { menu };
  })();

  const applyPhases = () => {
    const now = Date.now();
    document.querySelectorAll('[data-lsl-window]').forEach((w) => { w.dataset.phase = phaseOf(w, now); });

    document.querySelectorAll('[data-lsl-time]').forEach((el) => {
      const w = windowOf(el);
      if (!w) return;
      const p = w.dataset.phase;
      const from = parse(w.dataset.lslFrom);
      const to = parse(w.dataset.lslTo);
      const label = el.dataset['label' + p[0].toUpperCase() + p.slice(1)] || '';
      el.querySelector('[data-lsl-time-label]').textContent = label;
      const ts = p === 'upcoming' ? from : to || from;
      if (ts) el.querySelector('[data-lsl-time-value]').textContent = fmt(ts, p !== 'upcoming');
      el.hidden = !label && !ts;
      setupCountdown(el, p, p === 'upcoming' ? from : p === 'live' ? to : null, ts ? fmt(ts, p !== 'upcoming') : '');
    });

    document.querySelectorAll('[data-lsl-state]').forEach((el) => {
      const w = windowOf(el);
      if (!w) return;
      const p = w.dataset.phase;
      el.dataset.phase = p;
      el.textContent = el.dataset['label' + p[0].toUpperCase() + p.slice(1)] || '';
    });

    document.querySelectorAll('[data-lsl-phase-text]').forEach((el) => {
      const w = windowOf(el);
      if (!w) return;
      /* only when the phase changes the words: rewriting every 30 s threw away the heading's number
         spacing and its split words. A heading gets its numbers spaced again. */
      const t = el.dataset[w.dataset.phase] || '';
      if (el.textContent === t) return;
      if (el.matches('.lsl :is(h1, h2, h3)')) { el.textContent = ''; el.append(numFrag(t)); } else el.textContent = t;
    });

    document.querySelectorAll('[data-lsl-show]').forEach((el) => {
      const w = windowOf(el);
      if (w) el.hidden = !el.dataset.lslShow.split(' ').includes(w.dataset.phase);
    });

    document.querySelectorAll('[data-lsl-ended-text]').forEach((el) => {
      const w = windowOf(el);
      const to = w && parse(w.dataset.lslTo);
      if (to) el.textContent = (el.dataset.template || '').replace('[date]', fmtDay(to, true));
    });

    document.querySelectorAll('[data-lsl-buy]').forEach((btn) => {
      if (btn.getAttribute('aria-busy') === 'true') return;
      const w = windowOf(btn);
      const p = w ? w.dataset.phase : 'live';
      const available = btn.dataset.available === '1';
      let key = 'labelLive';
      if (p === 'upcoming') key = 'labelUpcoming';
      else if (p === 'ended') key = 'labelEnded';
      else if (!available) key = 'labelSoldout';
      btn.disabled = key !== 'labelLive';
      const text = btn.querySelector('[data-lsl-buy-text]');
      if (text) text.textContent = btn.dataset[key] || '';
    });
    document.querySelectorAll('[data-lsl-drops]').forEach((root) => root._lslDrops?.());
  };

  /* Daily drops: one envelope per day. Shows today's panel (else the next sealed day, else the
     last one) until the visitor picks another envelope; a picked envelope replays its opening
     (motion on, pointer only: arrow keys switch instantly). Also: opened count + countdown to the
     next envelope, a progress bar under each envelope, "Back to today's drop", calendar files. */
  const pad2 = (n) => String(n).padStart(2, '0');
  const initDrops = () => {
    document.querySelectorAll('[data-lsl-drops]:not([data-lsl-ready])').forEach((root) => {
      root.dataset.lslReady = '';
      const items = [...root.querySelectorAll('[data-lsl-drop]')];
      const btns = items.map((li) => li.querySelector('[data-lsl-drop-btn]'));
      const panels = [...root.querySelectorAll('[data-lsl-drop-panel]')];
      const opened = root.querySelector('[data-lsl-dopened]');
      const next = root.querySelector('[data-lsl-dnext]');
      const dayUnit = root.dataset.unitDay || 'd';
      let picked = -1;
      let cur = -1;
      const liveIndex = () => items.findIndex((li) => li.dataset.phase === 'live');
      const replay = (pn) => {
        if (!pn || !motionOn()) return;
        pn.classList.add('is-closed');
        void pn.offsetWidth;
        requestAnimationFrame(() => requestAnimationFrame(() => pn.classList.remove('is-closed')));
      };
      const show = (i, { scroll = false, animate = false } = {}) => {
        btns.forEach((b, n) => b.setAttribute('aria-pressed', n === i ? 'true' : 'false'));
        panels.forEach((pn, n) => { pn.hidden = n !== i; });
        items.forEach((li, n) => li.classList.toggle('is-current', n === i));
        if (animate && i !== cur) replay(panels[i]);
        cur = i;
        if (scroll) {
          const row = items[i]?.parentElement;
          const li = items[i];
          if (row && li && row.scrollWidth > row.clientWidth) {
            row.scrollTo({ left: li.offsetLeft - (row.clientWidth - li.offsetWidth) / 2, behavior: reduced.matches ? 'auto' : 'smooth' });
          }
        }
      };
      const auto = () => {
        let i = liveIndex();
        if (i < 0) i = items.findIndex((li) => li.dataset.phase === 'upcoming');
        if (i < 0) i = items.length - 1;
        return i;
      };
      const left = (ms) => {
        const s = Math.max(0, Math.floor(ms / 1000));
        const d = Math.floor(s / 86400);
        const hms = `${pad2(Math.floor((s % 86400) / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
        return d ? `${d}${dayUnit} ${hms}` : hms;
      };
      const tick = () => {
        const now = Date.now();
        items.forEach((li) => {
          const bar = li.querySelector('[data-lsl-dbar]');
          if (!bar) return;
          let f = 0;
          if (li.dataset.phase === 'ended') f = 1;
          else if (li.dataset.phase === 'live') {
            const a = parse(li.dataset.lslFrom);
            const b = parse(li.dataset.lslTo);
            if (a && b && b > a) f = Math.min(1, Math.max(0, (now - a) / (b - a)));
          }
          bar.style.setProperty('--f', f.toFixed(4));
        });
        if (opened) opened.textContent = pad2(items.filter((li) => li.dataset.phase !== 'upcoming').length);
        if (next) {
          const up = items.find((li) => li.dataset.phase === 'upcoming');
          const live = items[liveIndex()];
          const target = up ? parse(up.dataset.lslFrom) : live ? parse(live.dataset.lslTo) : null;
          const key = up ? 'labelNext' : live ? 'labelLast' : 'labelDone';
          next.querySelector('[data-lsl-dnext-label]').textContent = next.dataset[key] || '';
          next.classList.toggle('is-done', !target);
          if (target) next.querySelector('[data-lsl-dnext-value]').textContent = left(target - now);
        }
        const hasLive = liveIndex() >= 0;
        root.querySelectorAll('[data-lsl-djump]').forEach((b) => b.classList.toggle('is-off', !hasLive));
      };
      root._lslDrops = () => {
        if (picked < 0) { const i = auto(); show(i, { scroll: i !== cur }); }
        tick();
      };

      btns.forEach((b, i) => {
        b.addEventListener('click', (e) => { picked = i; show(i, { scroll: true, animate: e.detail !== 0 }); });
        b.addEventListener('keydown', (e) => {
          const k = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
          let n = null;
          if (k) n = (i + k + btns.length) % btns.length;
          else if (e.key === 'Home') n = 0;
          else if (e.key === 'End') n = btns.length - 1;
          if (n === null) return;
          e.preventDefault();
          btns[n].focus();
          picked = n;
          show(n, { scroll: true });
        });
      });

      root.addEventListener('click', (e) => {
        if (e.target.closest('[data-lsl-djump]')) {
          const i = liveIndex();
          if (i < 0) return;
          picked = -1;
          show(i, { scroll: true, animate: e.detail !== 0 });
          btns[i]?.focus({ preventScroll: true });
          return;
        }
        const cal = e.target.closest('[data-lsl-ics]');
        if (cal) downloadIcs(cal);
      });

      if (Number.isFinite(root._lslBlock)) { picked = root._lslBlock; show(picked); } else show(auto(), { scroll: true });
      tick();

      /* Opening plays once when the section comes into view (only if it starts below the fold). */
      const first = panels[cur];
      if (first && motionOn() && first.getBoundingClientRect().top > window.innerHeight * 0.85 && 'IntersectionObserver' in window) {
        first.classList.add('is-closed');
        const io = new IntersectionObserver((entries) => {
          if (!entries.some((en) => en.isIntersecting)) return;
          io.disconnect();
          panels.forEach((pn) => pn.classList.remove('is-closed'));
        }, { threshold: 0.35 });
        io.observe(first);
      }

      let onScreen = false;
      if ('IntersectionObserver' in window) new IntersectionObserver((en) => { onScreen = en.some((x) => x.isIntersecting); if (onScreen) tick(); }).observe(root);
      else onScreen = true;
      setInterval(() => { if (onScreen && !document.hidden) tick(); }, 1000);
    });
  };

  /* Calendar entry for a sealed day: one hour from the day's opening, 10-minute reminder. */
  const downloadIcs = (btn) => {
    const w = windowOf(btn);
    const from = w && parse(w.dataset.lslFrom);
    if (!from) return;
    const anchor = btn.closest('[data-lsl-drops]')?.id;
    const url = location.origin + location.pathname + location.search + (anchor ? '#' + anchor : '');
    cal.menu(btn, { title: btn.dataset.title || '', start: from, end: from + 3600000, details: btn.dataset.text, url, alarm: 10, file: btn.dataset.file || 'drop' });
  };

  /* Theme editor: selecting a Day block shows its panel. */
  document.addEventListener('shopify:block:select', (e) => {
    const li = e.target.closest?.('[data-lsl-drop]');
    const root = li?.closest('[data-lsl-drops]');
    if (!root) return;
    const i = [...root.querySelectorAll('[data-lsl-drop]')].indexOf(li);
    root._lslBlock = i;
    li.querySelector('[data-lsl-drop-btn]')?.click();
  });

  /* Copy buttons: data-lsl-copy = text to copy, data-label / data-copied = button labels. */
  const initCopy = () => {
    if (initCopy.done) return;
    initCopy.done = true;
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-lsl-copy]');
      if (!btn) return;
      const done = () => {
        btn.textContent = btn.dataset.copied || btn.dataset.label || '';
        btn.classList.add('is-copied');
        clearTimeout(btn._lslT);
        btn._lslT = setTimeout(() => { btn.textContent = btn.dataset.label || ''; btn.classList.remove('is-copied'); }, 1800);
      };
      const text = btn.dataset.lslCopy || '';
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, () => {});
      else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;opacity:0';
        document.body.append(ta);
        ta.select();
        try { document.execCommand('copy'); done(); } catch (err) { /* nothing to do */ }
        ta.remove();
      }
    });
  };

  /* Countdown: ticks every second while on screen. Changed digits roll when motion is on. */
  const clocks = new Set();
  const roll = (n, v) => {
    if (n.dataset.v === v) return;
    const first = n.dataset.v === undefined;
    n.dataset.v = v;
    if (first || !G || !motionOn()) { n.textContent = v; return; }
    const out = document.createElement('span');
    out.className = 'lsl-roll lsl-roll--out';
    out.textContent = n.querySelector('.lsl-roll:not(.lsl-roll--out)')?.textContent ?? n.textContent;
    const inn = document.createElement('span');
    inn.className = 'lsl-roll';
    inn.textContent = v;
    n.replaceChildren(out, inn);
    G.fromTo(out, { yPercent: 0, opacity: 1 }, { yPercent: -100, opacity: 0, duration: 0.5, ease: 'power3.inOut', onComplete: () => out.remove() });
    G.fromTo(inn, { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5, ease: 'power3.inOut' });
  };
  /* A countdown at zero re-reads the phases once, just after the boundary: calling applyPhases from the
     tick re-entered it (applyPhases -> setupCountdown -> tick) in the last second before every boundary */
  let phaseT = 0;
  const phaseAt = (target) => {
    if (phaseT) return;
    phaseT = setTimeout(() => { phaseT = 0; applyPhases(); }, Math.max(0, target - Date.now()) + 50);
  };
  const tickClock = (el) => {
    const p = el.dataset.phase;
    const from = parse(el.dataset.lslFrom);
    const to = parse(el.dataset.lslTo);
    const bar = el.querySelector('[data-lsl-progress]');
    if (bar && from && to) bar.style.setProperty('--p', Math.min(1, Math.max(0, (Date.now() - from) / (to - from))).toFixed(4));
    const target = p === 'upcoming' ? from : to;
    if (!target || p === 'ended') return;
    let s = Math.max(0, Math.floor((target - Date.now()) / 1000));
    if (config().lslPreview) s = 0;
    const set = (u, v) => { const n = el.querySelector(`[data-u="${u}"]`); if (n) roll(n, String(v).padStart(2, '0')); };
    set('d', Math.floor(s / 86400));
    set('h', Math.floor(s / 3600) % 24);
    set('m', Math.floor(s / 60) % 60);
    set('s', s % 60);
    if (s === 0 && !config().lslPreview) phaseAt(target);
  };
  /* Countdowns inside time lines (snippets/ls-sale-time): style from the line's data-cd, else the
     page default. Labels and units come from ls-sale-colors. */
  const timers = new Map();
  const timerIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((e) => { const t = timers.get(e.target); if (t) t.visible = e.isIntersecting; });
  }) : null;
  const units = (key, fallback) => {
    const u = (config()[key] || fallback).split(',').map((x) => x.trim());
    return fallback.split(',').map((f, i) => u[i] || f);
  };
  const cdStyle = (el) => {
    let st = el.dataset.cd || 'default';
    if (st === 'default') st = config().lslCd || 'chip';
    if (st === 'blocks' && el.closest('.lsl-ticket__facts')) st = 'chip';
    return ['text', 'chip', 'blocks'].includes(st) ? st : 'off';
  };
  function setupCountdown(el, phase, target, dateText) {
    const box = el.querySelector('[data-lsl-time-cd]');
    if (!box) return;
    const c = config();
    let st = cdStyle(el);
    if (phase === 'upcoming' && c.lslCdUpcoming === 'false' && !el.closest('[data-cd-always]')) st = 'off';
    if (!target || st === 'off' || phase === 'ended') {
      timers.delete(el);
      el.classList.remove('lsl-time--cd', 'lsl-time--keep', 'is-soon');
      delete el.dataset.cdStyle;
      box.hidden = true;
      el.querySelector('[data-lsl-time-value]').hidden = false;
      el.removeAttribute('title');
      return;
    }
    if (el.dataset.cdStyle !== st) {
      el.dataset.cdStyle = st;
      const long = units('lslCdUnitsLong', 'Days,Hrs,Min,Sec');
      box.innerHTML = st === 'blocks'
        ? ['d', 'h', 'm', 's'].map((u, i) => `<span class="lsl-cd__b"><strong data-u="${u}">00</strong><small>${long[i].replace(/[<>&"]/g, '')}</small></span>`).join('')
        : '<strong class="lsl-cd__t" data-cd-text></strong>';
    }
    el.classList.add('lsl-time--cd');
    /* "Keep the date": the date stays on the line and the countdown takes its own row under it */
    const keep = c.lslCdKeep === 'true';
    el.classList.toggle('lsl-time--keep', keep);
    el.querySelector('[data-lsl-time-value]').hidden = !keep;
    box.hidden = false;
    if (dateText) el.title = dateText;
    const t = timers.get(el) || { visible: !timerIO };
    t.phase = phase;
    t.target = target;
    timers.set(el, t);
    if (timerIO) timerIO.observe(el);
    tickCountdown(el, t);
  }
  const tickCountdown = (el, t) => {
    const c = config();
    let s = Math.max(0, Math.floor((t.target - Date.now()) / 1000));
    const soonH = Number(c.lslCdSoon || 0);
    const soon = t.phase === 'live' && soonH > 0 && s < soonH * 3600;
    el.classList.toggle('is-soon', soon);
    /* a section can keep its own wording (data-cd-own-label); otherwise the page-wide countdown labels apply */
    const label = el.closest('[data-cd-own-label]') || el.classList.contains('lsl-time--keep') ? '' : t.phase === 'upcoming' ? c.lslCdUpcomingLabel : (soon && c.lslCdSoonLabel) || c.lslCdLiveLabel;
    const lab = el.querySelector('[data-lsl-time-label]');
    if (label && lab.textContent !== label) lab.textContent = label;
    const d = Math.floor(s / 86400), h = Math.floor(s / 3600) % 24, m = Math.floor(s / 60) % 60, sec = s % 60;
    const pad = (v) => String(v).padStart(2, '0');
    const txt = el.querySelector('[data-cd-text]');
    if (txt) {
      const u = units('lslCdUnits', 'd,h,m,s');
      txt.textContent = (d ? `${d}${u[0]} ` : '') + `${pad(h)}${u[1]} ${pad(m)}${u[2]} ${pad(sec)}${u[3]}`;
    } else {
      [['d', d], ['h', h], ['m', m], ['s', sec]].forEach(([k, v]) => {
        const n = el.querySelector(`[data-u="${k}"]`);
        if (n) roll(n, pad(v));
      });
    }
    if (s === 0 && !c.lslPreview) phaseAt(t.target);
  };
  setInterval(() => {
    if (document.hidden) return;
    timers.forEach((t, el) => {
      if (!el.isConnected) { timers.delete(el); return; }
      if (t.visible) tickCountdown(el, t);
    });
  }, 1000);

  const initClocks = () => {
    const els = document.querySelectorAll('[data-lsl-clock]');
    if (!els.length) return;
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? clocks.add(e.target) : clocks.delete(e.target)));
    }) : null;
    els.forEach((el) => { tickClock(el); io ? io.observe(el) : clocks.add(el); });
    setInterval(() => { if (!document.hidden) clocks.forEach(tickClock); }, 1000);
  };

  /* Section menu: hide links without a target, mark the section in view. */
  /* RC picker: a tier pick swaps the stage card and the receipt (data-pick; the rest is CSS). Arrow keys move. */
  const initRcPick = () => {
    document.querySelectorAll('[data-lsl-rcpick]:not([data-rcp-ready])').forEach((root) => {
      root.dataset.rcpReady = '';
      const picks = [...root.querySelectorAll('[data-rcp-pick]')];
      const pick = (btn, focus) => {
        root.dataset.pick = btn.dataset.rcpPick;
        picks.forEach((b) => { b.setAttribute('aria-checked', String(b === btn)); b.tabIndex = b === btn ? 0 : -1; });
        if (focus) btn.focus();
      };
      picks.forEach((b, i) => {
        b.tabIndex = b.getAttribute('aria-checked') === 'true' ? 0 : -1;
        b.addEventListener('click', () => pick(b));
        b.addEventListener('keydown', (e) => {
          const d = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          pick(picks[(i + d + picks.length) % picks.length], true);
        });
      });
      /* ?rc=2 (the homepage Reservation Cards slide) opens with that tier picked */
      const want = new URLSearchParams(location.search).get('rc');
      const wanted = want && picks.find((b) => b.dataset.rcpPick === want);
      if (wanted) pick(wanted);
      const steps = root.querySelector('.lsl-rcp__steps');
      if (steps) steps.style.setProperty('--n', Math.min(steps.children.length, 4));
    });
  };

  /* Lucky numbers: "check your order number" compares its last digit with each card's lucky digit */
  const initLucky = () => {
    document.querySelectorAll('[data-lucky-check]:not([data-lsl-ready])').forEach((chk) => {
      chk.dataset.lslReady = '';
      const root = chk.closest('.lsl-lucky') || document;
      const input = chk.querySelector('input');
      const out = chk.querySelector('[data-lucky-result]');
      const cards = [...root.querySelectorAll('.lsl-lucky__card[data-digit]')];
      const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const fillB = (s, o) => esc(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in o ? '<b>' + esc(o[k]) + '</b>' : m));
      const check = () => {
        const digits = (input.value || '').replace(/\D/g, '');
        let hit = null;
        cards.forEach((c) => { const m = !!digits && c.dataset.digit === digits.slice(-1); c.classList.toggle('is-match', m); if (m && !hit) hit = c; });
        out.classList.toggle('is-win', !!hit);
        if (!digits) { out.innerHTML = input.value.trim() ? esc(chk.dataset.bad) : ''; return; }
        const d = digits.slice(-1);
        out.innerHTML = hit ? fillB(chk.dataset.win, { digit: d, dates: hit.dataset.dates || '', title: hit.dataset.title || '' }) : fillB(chk.dataset.lose, { digit: d });
      };
      chk.addEventListener('submit', (e) => { e.preventDefault(); check(); });
      input.addEventListener('input', () => { if (!input.value.trim()) check(); });
    });
  };

  /* Launch day: four phases from three moments (reservation closes, launch opens, offer ends) */
  const initLaunch = () => {
    document.querySelectorAll('[data-lsl-launch]:not([data-lsl-ready])').forEach((root) => {
      root.dataset.lslReady = '';
      const t0 = root.dataset.t0 ? parse(root.dataset.t0) : null;
      const t1 = parse(root.dataset.t1);
      const t2 = parse(root.dataset.t2);
      const t3 = parse(root.dataset.t3);
      const lab = root.querySelector('[data-ln-clock-label]');
      const wrap = root.querySelector('[data-ln-clock-wrap]');
      const u = {};
      root.querySelectorAll('[data-u]').forEach((n) => { u[n.dataset.u] = n; });
      /* 'before' lights no step: the first step is the reservation window itself */
      const order = ['before', 'warmup', 'gap', 'sale'];
      const pad = (v) => String(v).padStart(2, '0');
      const tick = () => {
        const now = Date.now();
        const ph = t0 && now < t0 ? 'before' : t1 && now < t1 ? 'warmup' : t2 && now < t2 ? 'gap' : t3 && now < t3 ? 'sale' : 'ended';
        if (root.dataset.phaseLn !== ph) {
          root.dataset.phaseLn = ph;
          root.querySelectorAll('[data-ln-show]').forEach((n) => { n.hidden = !n.dataset.lnShow.split(' ').includes(ph); });
          const i = order.indexOf(ph);
          root.querySelectorAll('.lsl-ln__step[data-k]').forEach((st) => {
            const k = order.indexOf(st.dataset.k);
            st.classList.toggle('is-now', k === i);
            st.classList.toggle('is-past', i === -1 || k < i);
          });
          if (lab) lab.textContent = root.dataset['l' + ph[0].toUpperCase() + ph.slice(1)] || '';
          if (wrap) wrap.hidden = ph === 'ended';
        }
        const target = ph === 'before' ? t0 : ph === 'warmup' ? t1 : ph === 'gap' ? t2 : ph === 'sale' ? t3 : null;
        if (!target) return;
        const s = Math.max(0, Math.floor((target - now) / 1000));
        const set = (k, v) => { if (u[k] && u[k].textContent !== v) u[k].textContent = v; };
        set('d', pad(Math.floor(s / 86400)));
        set('h', pad(Math.floor(s / 3600) % 24));
        set('m', pad(Math.floor(s / 60) % 60));
        set('s', pad(s % 60));
      };
      tick();
      setInterval(tick, 1000);
      root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ln-ics]');
        if (!btn || !t2) return;
        const url = location.href.split('#')[0] + '#' + root.id;
        cal.menu(btn, { title: root.dataset.icsTitle, start: t2, end: t2 + 3600000, details: root.dataset.icsText, url, alarm: 10, file: root.dataset.icsFile || 'launch' });
      });
    });
    /* ladder bars rise once on screen */
    document.querySelectorAll('[data-lsl-ladder]:not(.is-in)').forEach((l) => {
      if (!('IntersectionObserver' in window)) { l.classList.add('is-in'); return; }
      const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) { l.classList.add('is-in'); io.disconnect(); } }, { threshold: 0.25 });
      io.observe(l);
    });
  };

  /* Floating menu: shown only while it is stuck under the header (it has no height in the page) */
  const initNavFloat = () => {
    document.querySelectorAll('.lsl-nav--float:not([data-lsl-float])').forEach((nav) => {
      nav.dataset.lslFloat = '';
      const sec = nav.closest('.ls-sale-nav-section') || nav;
      let ticking = false;
      const place = () => {
        ticking = false;
        const off = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ls-header-offset')) || 0;
        nav.classList.toggle('is-shown', sec.getBoundingClientRect().top <= off + 16);
      };
      addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(place); } }, { passive: true });
      addEventListener('resize', place, { passive: true });
      place();
    });
  };

  const initNav = () => {
    document.querySelectorAll('[data-lsl-nav]').forEach((nav) => {
      const links = [...nav.querySelectorAll('a[href^="#"]')];
      const pairs = [];
      links.forEach((a) => {
        let target = null;
        try { target = document.querySelector(a.getAttribute('href')); } catch (e) { target = null; }
        if (!target) { a.closest('li').hidden = true; return; }
        pairs.push([a, target]);
      });
      if (!pairs.length) { nav.closest('.ls-sale-section')?.setAttribute('hidden', ''); return; }
      const list = nav.querySelector('.lsl-nav__list');
      const ind = nav.querySelector('[data-lsl-ind]');
      const pill = nav.classList.contains('lsl-nav--pill') && ind;
      if (pill) nav.classList.add('has-ind');
      let shown = null;
      const place = (a, instant) => {
        if (!pill) return;
        const to = a
          ? { x: a.offsetLeft, width: a.offsetWidth, opacity: 1 }
          : { opacity: 0 };
        if (G && !instant && motionOn()) G.to(ind, { ...to, duration: 0.45, ease: 'power3.out', overwrite: true });
        else if (G) G.set(ind, to);
        else {
          if (a) { ind.style.transform = `translateX(${to.x}px)`; ind.style.width = to.width + 'px'; }
          ind.style.opacity = String(to.opacity);
        }
      };
      const update = () => {
        const line = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ls-header-offset')) || 0) + nav.offsetHeight + 24;
        let current = null;
        pairs.forEach(([a, t]) => { if (t.getBoundingClientRect().top <= line) current = a; });
        pairs.forEach(([a]) => {
          if (a === current) {
            if (!a.hasAttribute('aria-current')) {
              a.setAttribute('aria-current', 'true');
              const l = a.offsetLeft;
              if (l < list.scrollLeft || l + a.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollTo({ left: l - 16, behavior: reduced.matches ? 'auto' : 'smooth' });
            }
          } else a.removeAttribute('aria-current');
        });
        if (current !== shown) { place(current, shown === null && !current); shown = current; }
      };
      let raf = 0;
      window.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      new ResizeObserver(() => place(shown, true)).observe(list);
      nav._lslUpdate = () => place(shown, true);
      update();
    });
  };

  /* Highlight rail arrows, shown only when it overflows. */
  const initRails = () => {
    document.querySelectorAll('[data-lsl-rail-root]:not([data-lsl-ready])').forEach((root) => {
      root.dataset.lslReady = '';
      const rail = root.querySelector('[data-lsl-rail]');
      const arrows = root.querySelector('[data-lsl-arrows]');
      if (!rail || !arrows) return;
      const prev = arrows.querySelector('[data-lsl-prev]');
      const next = arrows.querySelector('[data-lsl-next]');
      const sync = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        arrows.hidden = max < 4;
        prev.disabled = rail.scrollLeft < 4;
        next.disabled = rail.scrollLeft > max - 4;
      };
      const step = (dir) => {
        const card = rail.firstElementChild;
        const w = card ? card.getBoundingClientRect().width + 16 : rail.clientWidth * 0.8;
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        rail.scrollBy({ left: dir * w, behavior: reduce ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      rail.addEventListener('scroll', sync, { passive: true });
      new ResizeObserver(() => { sync(); rail.classList.toggle('can-drag', rail.scrollWidth - rail.clientWidth > 4); }).observe(rail);
      sync();

      /* Mouse drag to scroll (touch already swipes natively). */
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      let down = false;
      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || !rail.classList.contains('can-drag')) return;
        down = true; moved = false; startX = e.clientX; startLeft = rail.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 5) { moved = true; rail.classList.add('is-drag'); }
        if (moved) rail.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (!moved) return;
        rail.classList.remove('is-drag');
        const card = rail.firstElementChild;
        const w = card ? card.getBoundingClientRect().width + 16 : 1;
        rail.scrollTo({ left: Math.round(rail.scrollLeft / w) * w, behavior: reduced.matches ? 'auto' : 'smooth' });
        suppress = true;
        setTimeout(() => { suppress = false; }, 0);
      });
      let suppress = false;
      rail.addEventListener('click', (ev) => { if (suppress) { ev.preventDefault(); ev.stopPropagation(); } }, true);
      rail.addEventListener('dragstart', (e) => e.preventDefault());
    });
  };

  /* Drawers */
  const initDrawers = () => {
    document.addEventListener('click', (e) => {
      const open = e.target.closest('[data-lsl-drawer-open]');
      if (open) {
        const d = document.getElementById(open.dataset.lslDrawerOpen);
        if (d && typeof d.showModal === 'function') {
          e.preventDefault();
          d._lslTrigger = open;
          d.showModal();
          d.querySelector('.lsl-drawer__scroll')?.scrollTo(0, 0);
          document.documentElement.classList.add('lsl-lock');
        }
        return;
      }
      if (e.target.closest('[data-lsl-drawer-close]')) {
        e.target.closest('dialog')?.close();
        return;
      }
      if (e.target.matches?.('dialog.lsl-drawer')) e.target.close();
    });
    document.querySelectorAll('[data-lsl-drawer]').forEach((d) => {
      d.addEventListener('close', () => {
        if (!document.querySelector('dialog.lsl-drawer[open]')) document.documentElement.classList.remove('lsl-lock');
        d._lslTrigger?.focus({ preventScroll: true });
      });
    });
  };

  /* Timed add-to-cart (reservation cards, mystery boxes) */
  const initBuy = () => {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-lsl-buy]');
      if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
      const text = btn.querySelector('[data-lsl-buy-text]');
      btn.setAttribute('aria-busy', 'true');
      const drawer = document.querySelector('m-cart-drawer');
      const useDrawer = drawer && window.MinimogSettings?.enable_cart_drawer && typeof drawer.renderContents === 'function';
      const body = new FormData();
      body.append('id', btn.dataset.variant);
      body.append('quantity', '1');
      if (useDrawer) {
        body.append('sections', drawer.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
      }
      try {
        const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const result = await res.json();
        if (!res.ok || result.status) throw new Error(result.description || result.message || 'add failed');
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result } }));
        if (useDrawer) {
          drawer.renderContents(result);
          drawer.getCart?.().then((cart) => cart && drawer.updateCartCount(cart.item_count));
        } else {
          window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
        }
      } catch (err) {
        console.warn(err);
        if (text) text.textContent = btn.dataset.labelError || '';
        setTimeout(applyPhases, 2600);
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });
  };

  /* Product grid filters + sort. The type chips, the brand and price menus combine (AND); "Show
     more" caps how many of the matching products are visible and resets whenever the view changes. */
  /* "Browse whole collections" mode: each tab is a collection and Shopify filters, sorts and pages
     it. The grid is swapped for pages of sections/ls-sale-feed.liquid fetched on the collection URL.
     The first page is printed by the section; the first fetch (when the grid nears the viewport)
     only replaces it with the same products plus the collection's brand list. */
  const initLiveProducts = (root, grid) => {
    const buttons = [...root.querySelectorAll('[data-lsl-filter]')];
    const vendorSel = root.querySelector('[data-lsl-select="vendor"]');
    const priceSel = root.querySelector('[data-lsl-select="price"]');
    const sort = root.querySelector('[data-lsl-sort]');
    const more = root.querySelector('[data-lsl-more]');
    const moreWrap = more?.parentElement;
    const empty = root.querySelector('[data-lsl-empty]');
    const count = root.querySelector('[data-lsl-count]');
    const base = (window.Shopify?.routes?.root || '/') + 'collections/';
    let handle = root.dataset.main;
    let next = '';
    let ctrl = null;
    let page = 1;
    const allBrands = vendorSel?.options[0]?.textContent || '';

    // Shopify's collection price filter is unavailable outside the shop currency.
    // Read every page of a lightweight, market-priced index only when a price band is selected,
    // then render just the visible cards. Unfiltered browsing keeps its existing server paging.
    const priceIndexes = new Map();
    const priceCards = new Map();
    const currency = () => window.Shopify?.currency?.active || '';
    const withCurrency = (q) => { if (currency()) q.set('currency', currency()); return q; };
    const priceIndex = async (signal) => {
      const order = sort?.value || 'featured';
      const key = [handle, order, currency()].join('|');
      if (priceIndexes.has(key)) return priceIndexes.get(key);
      const products = [];
      let number = 1;
      while (true) {
        const q = withCurrency(new URLSearchParams({ section_id: 'ls-sale-price-index', page: number }));
        if (order !== 'featured') q.set('sort_by', order);
        const res = await fetch(base + handle + '?' + q, { signal, credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const data = JSON.parse(doc.querySelector('[data-lsl-price-index]')?.textContent || 'null');
        if (!data || !Array.isArray(data.products) || (currency() && data.currency !== currency())) throw new Error('invalid price index');
        products.push(...data.products);
        if (!data.next) break;
        if (Number(data.next) !== number + 1) throw new Error('invalid index page');
        number += 1;
      }
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      priceIndexes.set(key, products);
      return products;
    };
    const priceCard = async (product, signal) => {
      const key = [product.handle, currency()].join('|');
      let html = priceCards.get(key);
      if (!html) {
        const q = withCurrency(new URLSearchParams({ section_id: 'ls-sale-card' }));
        const url = (window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(product.handle) + '?' + q;
        const res = await fetch(url, { signal, credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const card = doc.querySelector('.lsl-item');
        if (!card || Number(card.dataset.price) !== product.price) throw new Error('price card changed');
        html = card.outerHTML;
        priceCards.set(key, html);
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return document.importNode(doc.querySelector('.lsl-item'), true);
    };
    const priceResults = async (p, signal) => {
      const products = await priceIndex(signal);
      const [lo, hi] = priceSel.value.split('-');
      const inBand = products.filter((item) => (root.dataset.sold || item.available)
        && item.price >= Number(lo || 0) && (!hi || item.price < Number(hi)));
      const matches = inBand.filter((item) => !vendorSel?.value || item.vendor === vendorSel.value);
      if (sort?.value === 'price-ascending') matches.sort((a, b) => a.price - b.price);
      if (sort?.value === 'price-descending') matches.sort((a, b) => b.price - a.price);
      const size = 8; // Keep the same page size as ls-sale-feed.
      const visible = matches.slice((p - 1) * size, p * size);
      const items = [];
      // Limit concurrent card renders; keep the collection's order despite request completion order.
      for (let i = 0; i < visible.length; i += 4) {
        items.push(...await Promise.all(visible.slice(i, i + 4).map((item) => priceCard(item, signal))));
      }
      return { items, total: matches.length, next: p * size < matches.length ? 'more' : '', brands: [...new Set(inBand.map((item) => item.vendor).filter(Boolean))] };
    };

    const query = (p) => {
      const q = new URLSearchParams({ section_id: root.dataset.feed });
      if (sort && sort.value !== 'featured') q.set('sort_by', sort.value);
      if (vendorSel?.value) q.append('filter.p.vendor', vendorSel.value);
      if (!root.dataset.sold) q.set('filter.v.availability', '1');
      if (p > 1) q.set('page', p);
      return base + handle + '?' + withCurrency(q);
    };
    const setBrands = (list) => {
      if (!vendorSel || !Array.isArray(list)) return;
      const cur = vendorSel.value;
      const names = [...new Set(cur ? [...list, cur] : list)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      vendorSel.replaceChildren(new Option(allBrands, ''), ...names.map((n) => new Option(n, n)));
      vendorSel.value = names.includes(cur) ? cur : '';
      vendorSel.closest('.lt-field')?.classList.toggle('is-set', !!vendorSel.value);
    };
    const load = async (append) => {
      ctrl?.abort();
      const mine = new AbortController();
      ctrl = mine;
      const p = append ? page + 1 : 1;
      root.classList.add('is-loading');
      try {
        let items, total, following, brands;
        if (priceSel?.value) {
          ({ items, total, next: following, brands } = await priceResults(p, mine.signal));
        } else {
          const res = await fetch(query(p), { signal: mine.signal, credentials: 'same-origin' });
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const feed = doc.querySelector('[data-feed]');
          if (!feed) throw new Error('no feed');
          items = [...feed.querySelectorAll('.lsl-item')].map((n) => document.importNode(n, true));
          total = Number(feed.dataset.total) || 0;
          following = feed.dataset.next || '';
          try { brands = JSON.parse(doc.querySelector('[data-feed-vendors]')?.textContent || 'null'); } catch (e) { /* keep the menu */ }
        }
        if (ctrl !== mine || mine.signal.aborted) return;
        page = p;
        next = following;
        root.querySelector('[data-lsl-load-error]')?.remove();
        if (append) grid.append(...items);
        else grid.replaceChildren(...items);
        if (!append) setBrands(brands);
        if (empty) empty.hidden = total > 0;
        if (moreWrap) moreWrap.hidden = !next;
        if (count) count.textContent = (total === 1 ? root.dataset.countOne : root.dataset.countMany || '').replace('[count]', total);
        if (append) items[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
        if (G && motionOn()) G.fromTo(items.slice(0, 12), { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', stagger: 0.035, overwrite: true, clearProps: 'transform' });
        if (ST) ST.refresh();
        spaceNumbers();
      } catch (e) {
        if (e.name !== 'AbortError' && ctrl === mine) {
          if (!append) {
            grid.replaceChildren(); // A failed request must not leave results from the old price band.
            if (empty) empty.hidden = true;
            if (count) count.textContent = '';
            next = '';
          }
          if (moreWrap) moreWrap.hidden = !next;
          if (!root.querySelector('[data-lsl-load-error]')) {
            const error = document.createElement('p');
            error.dataset.lslLoadError = '';
            error.className = 'lsl-empty';
            error.setAttribute('role', 'alert');
            error.textContent = 'Unable to load products. Please try again.';
            grid.after(error);
          }
        }
      } finally {
        if (ctrl === mine) root.classList.remove('is-loading'); /* an aborted request must not clear a newer one's state */
      }
    };
    const markSet = () => {
      [vendorSel, priceSel].forEach((sel) => sel?.closest('.lt-field')?.classList.toggle('is-set', !!sel.value));
      sort?.closest('.lt-field')?.classList.toggle('is-set', sort.value !== 'featured');
    };
    buttons.forEach((b) => b.addEventListener('click', () => {
      if (b.getAttribute('aria-pressed') === 'true') return;
      const f = b.dataset.lslFilter || '';
      handle = f.startsWith('collection:') ? f.slice(11) : root.dataset.main;
      buttons.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      if (vendorSel) vendorSel.value = '';
      markSet();
      load(false);
    }));
    [vendorSel, priceSel, sort].forEach((sel) => sel?.addEventListener('change', () => { markSet(); load(false); }));
    root.querySelector('[data-lsl-filter-reset]')?.addEventListener('click', () => {
      [vendorSel, priceSel].forEach((sel) => { if (sel) sel.value = ''; });
      if (sort) sort.value = 'featured';
      handle = root.dataset.main;
      buttons.forEach((x, i) => x.setAttribute('aria-pressed', String(i === 0)));
      markSet();
      load(false);
    });
    more?.addEventListener('click', () => { if (next && !root.classList.contains('is-loading')) load(true); });
    // First fetch once the grid is close, for the full brand list and the real "View more" state
    const io = new IntersectionObserver((es) => {
      if (!es.some((e) => e.isIntersecting)) return;
      io.disconnect();
      load(false);
    }, { rootMargin: '600px 0px' });
    io.observe(grid);
  };

  const initProducts = () => {
    document.querySelectorAll('[data-lsl-products]:not([data-lsl-ready])').forEach((root) => {
      root.dataset.lslReady = '';
      const grid = root.querySelector('[data-lsl-grid]');
      if (!grid) return;
      if (root.hasAttribute('data-lsl-live')) return initLiveProducts(root, grid);
      const items = [...grid.querySelectorAll('.lsl-item')];
      const empty = root.querySelector('[data-lsl-empty]');
      const count = root.querySelector('[data-lsl-count]');
      const buttons = [...root.querySelectorAll('[data-lsl-filter]')];
      const selects = [...root.querySelectorAll('[data-lsl-select]')];
      const sort = root.querySelector('[data-lsl-sort]');
      const more = root.querySelector('[data-lsl-more]');
      const pageSize = parseInt(root.dataset.pageSize, 10) || 0;
      let cap = pageSize || Infinity;
      let filter = '';
      const chipOk = (item, f = filter) => {
        if (!f) return true;
        const [kind, raw] = f.split(':');
        const values = (raw || '').split('|').filter(Boolean);
        if (kind === 'tag') return values.some((v) => item.dataset.tags.includes('|' + v + '|'));
        const field = kind === 'vendor' ? item.dataset.vendor : item.dataset.type;
        return values.includes(field);
      };
      const selectOk = (item) => selects.every((sel) => {
        const v = sel.value;
        if (!v) return true;
        if (sel.dataset.lslSelect === 'vendor') return item.dataset.vendor === v;
        const [lo, hi] = v.split('-');
        const price = Number(item.dataset.price);
        return price >= Number(lo || 0) && (hi === '' || hi === undefined || price < Number(hi));
      });
      const matches = (item) => chipOk(item) && selectOk(item);
      // A category chip with nothing behind it in this collection is hidden rather than leading to an empty grid
      buttons.forEach((b) => { if (b.dataset.lslFilter && !items.some((it) => chipOk(it, b.dataset.lslFilter))) b.hidden = true; });
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      const byName = (a, b) => collator.compare(a.dataset.name || '', b.dataset.name || '');
      const render = (animate = true) => {
        const mode = sort ? sort.value : 'featured';
        const sorted = [...items].sort((a, b) => {
          if (mode === 'price-asc') return a.dataset.price - b.dataset.price;
          if (mode === 'price-desc') return b.dataset.price - a.dataset.price;
          if (mode === 'discount') return b.dataset.off - a.dataset.off || a.dataset.index - b.dataset.index;
          if (mode === 'date-desc') return b.dataset.date - a.dataset.date || a.dataset.index - b.dataset.index;
          if (mode === 'date-asc') return a.dataset.date - b.dataset.date || a.dataset.index - b.dataset.index;
          if (mode === 'name-asc') return byName(a, b);
          if (mode === 'name-desc') return byName(b, a);
          return a.dataset.index - b.dataset.index;
        });
        let matched = 0;
        sorted.forEach((item) => {
          const ok = matches(item);
          if (ok) matched += 1;
          item.hidden = !ok || matched > cap;
          grid.appendChild(item);
        });
        if (empty) empty.hidden = matched > 0;
        if (more) more.parentElement.hidden = matched <= cap;
        if (count) count.textContent = (matched === 1 ? root.dataset.countOne : root.dataset.countMany || '').replace('[count]', matched);
        if (G && motionOn() && animate) {
          const visible = sorted.filter((i) => !i.hidden).slice(0, 12);
          G.fromTo(visible, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', stagger: 0.035, overwrite: true, clearProps: 'transform' });
          sorted.filter((i) => !i.hidden).slice(12).forEach((i) => G.set(i, { opacity: 1, y: 0 }));
        }
        if (ST) ST.refresh();
      };
      // A dropdown that narrows the list is outlined (.is-set), as on the brand pages' filter card
      const markSet = () => {
        selects.forEach((sel) => sel.closest('.lt-field')?.classList.toggle('is-set', !!sel.value));
        if (sort) sort.closest('.lt-field')?.classList.toggle('is-set', sort.value !== 'featured');
      };
      const changed = () => { cap = pageSize || Infinity; markSet(); render(); };
      buttons.forEach((b) => b.addEventListener('click', () => {
        if (b.getAttribute('aria-pressed') === 'true') return;
        filter = b.dataset.lslFilter;
        buttons.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        changed();
      }));
      selects.forEach((sel) => sel.addEventListener('change', changed));
      root.querySelector('[data-lsl-filter-reset]')?.addEventListener('click', () => {
        selects.forEach((sel) => { sel.value = ''; });
        filter = '';
        buttons.forEach((x, i) => x.setAttribute('aria-pressed', String(i === 0)));
        changed();
      });
      sort?.addEventListener('change', changed);
      more?.addEventListener('click', () => {
        const before = items.filter((i) => !i.hidden).length;
        cap += pageSize;
        render(false);
        items.filter((i) => !i.hidden)[before]?.querySelector('a[href]')?.focus({ preventScroll: true });
      });
      if (pageSize) render(false);
    });
  };

  /* Spend & save meter: a cart-total slider over the tiers. Stops sit at equal distances, so the
     slider maps piecewise between tier amounts. Amounts are read from the tier text ("$1,000+"). */
  const num = (t) => parseFloat(String(t || '').replace(/[^\d.]/g, '')) || 0;
  const meters = [];
  const initMeters = () => {
    document.querySelectorAll('[data-lsl-meter]:not([data-lsl-ready])').forEach((meter) => {
      meter.dataset.lslReady = '';
      const stops = [...meter.querySelectorAll('[data-lsl-meter-stop]')];
      const btns = [...meter.querySelectorAll('[data-lsl-meter-tier]')];
      const input = meter.querySelector('[data-lsl-meter-input]');
      const field = meter.querySelector('[data-lsl-meter-amount]');
      if (!stops.length || !input || !field) return;
      const first = stops[0].dataset.spend || '';
      const prefix = (first.match(/^[^\d]*/) || [''])[0].trim();
      const suffix = first.replace(/^[^\d]*[\d.,\s]*/, '').replace(/\+/g, '').trim();
      const lang = document.documentElement.lang || undefined;
      const fmt = (n) => Math.round(n).toLocaleString(lang);
      const money = (n) => prefix + fmt(n) + (suffix ? ' ' + suffix : '');
      const saveText = (t) => String(t || '').replace(/\s*off\b\.?/i, '').trim();
      const tiers = stops.map((el, i) => {
        const save = saveText(el.dataset.save);
        return { el, btn: btns[i], spend: num(el.dataset.spend), save, pct: /%/.test(save), value: num(save) };
      });
      const top = tiers[tiers.length - 1].spend * (parseFloat(meter.dataset.top) || 1.3);
      const marks = [0, ...tiers.map((t) => t.spend), top];
      const segs = marks.length - 1;
      const toMoney = (p) => {
        const x = Math.min(segs - 1e-9, Math.max(0, p * segs));
        const k = Math.floor(x);
        return marks[k] + (x - k) * (marks[k + 1] - marks[k]);
      };
      const toPos = (m) => {
        for (let k = 0; k < segs; k += 1) if (m <= marks[k + 1]) return (k + (m - marks[k]) / (marks[k + 1] - marks[k] || 1)) / segs;
        return 1;
      };
      const step = top > 400 ? 10 : 1;
      const snap = (c) => {
        let v = Math.round(c / step) * step;
        tiers.forEach((t) => { if (Math.abs(v - t.spend) <= step * 1.5) v = t.spend; });
        return v;
      };
      const q = (k) => meter.querySelector('[data-lsl-meter-' + k + ']');
      const saveEl = q('save');
      const payEl = q('pay');
      const nextEl = q('next');
      const liveEl = q('live');
      const goal = q('goal');
      const togo = q('togo');
      const unlock = q('unlock');
      const gap = q('gap');
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
      const fill = q('fill');
      const pre = q('prefix');
      const suf = q('suffix');
      if (pre) pre.textContent = prefix;
      if (suf) suf.textContent = suffix;
      const d = meter.dataset;
      const fieldWidth = () => { field.style.width = Math.max(1, field.value.length) + 0.4 + 'ch'; };
      const state = { cart: 0, tier: null, tween: null, quiet: true };
      const stop = () => { state.tween?.kill(); state.tween = null; };
      const render = (cart, from) => {
        cart = Math.max(0, cart);
        state.cart = cart;
        const reached = tiers.filter((t) => cart >= t.spend);
        const now = reached[reached.length - 1] || null;
        const next = tiers.find((t) => cart < t.spend);
        const off = now ? Math.min(cart, now.pct ? (cart * now.value) / 100 : now.value) : 0;
        if (from !== 'field') { field.value = fmt(cart); fieldWidth(); }
        saveEl.textContent = off ? '-' + money(off) : money(0);
        payEl.textContent = money(cart - off);
        meter.classList.toggle('is-saving', off > 0);
        nextEl.textContent = next
          ? (d.labelNext || '').replace('[amount]', money(next.spend - cart)).replace('[save]', next.save)
          : d.labelTop || '';
        if (goal) {
          const from = now ? now.spend : 0;
          goal.classList.toggle('is-top', !next);
          if (togo) togo.innerHTML = next
            ? esc(d.labelTogo || '[amount]').replace('[amount]', '<b>' + esc(money(next.spend - cart)) + '</b>')
            : esc(d.labelTop || '');
          if (unlock) unlock.textContent = next ? next.el.dataset.save || next.save : '';
          gap?.style.setProperty('--g', next ? ((cart - from) / ((next.spend - from) || 1)).toFixed(4) : '1');
        }
        tiers.forEach((t) => {
          const on = cart >= t.spend;
          t.el.classList.toggle('is-on', on);
          t.btn?.classList.toggle('is-on', on);
          t.btn?.classList.toggle('is-next', t === next);
          t.btn?.setAttribute('aria-pressed', String(t === now));
        });
        const p = toPos(Math.min(cart, top));
        fill.style.setProperty('--v', p.toFixed(4));
        if (from !== 'range') input.value = String(Math.round(p * 1000));
        const save = now ? now.save : money(0);
        input.setAttribute('aria-valuetext', (d.labelValue || '[cart] [save]').replace('[cart]', money(cart)).replace('[save]', save));
        if (now !== state.tier) {
          if (!state.quiet && now && !reduced.matches) {
            saveEl.animate?.([{ transform: 'translateY(6px)', opacity: 0.3 }, { transform: 'none', opacity: 1 }], { duration: 320, easing: 'cubic-bezier(.23,1,.32,1)' });
            t0(now);
          }
          if (!state.quiet && liveEl) liveEl.textContent = now ? (d.labelSave || '[save]').replace('[save]', now.save) : '';
          state.tier = now;
        }
      };
      const t0 = (t) => t.btn?.animate?.([{ transform: 'translate(-50%,0) scale(1)' }, { transform: 'translate(-50%,0) scale(1.06)' }, { transform: 'translate(-50%,0) scale(1)' }], { duration: 360, easing: 'cubic-bezier(.23,1,.32,1)' });
      const glide = (to, dur) => {
        stop();
        if (!G || !motionOn() || !dur) { render(to); return; }
        const o = { c: state.cart };
        state.tween = G.to(o, { c: to, duration: dur, ease: 'power3.out', onUpdate: () => render(snap(o.c)), onComplete: () => { state.tween = null; render(to); } });
      };
      input.addEventListener('input', () => { stop(); state.quiet = false; render(snap(toMoney(input.value / 1000)), 'range'); });
      ['pointerdown', 'keydown', 'touchstart'].forEach((ev) => input.addEventListener(ev, stop, { passive: true }));
      field.addEventListener('focus', () => { stop(); field.select(); });
      field.addEventListener('input', () => {
        state.quiet = false;
        const clean = field.value.replace(/[^\d.,]/g, '');
        if (clean !== field.value) field.value = clean;
        fieldWidth();
        render(num(clean.replace(/,/g, '')), 'field');
      });
      field.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); field.blur(); }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          state.quiet = false;
          render(Math.max(0, state.cart + (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1)));
        }
      });
      field.addEventListener('blur', () => render(state.cart));
      tiers.forEach((t) => t.btn?.addEventListener('click', (e) => { state.quiet = false; glide(t.spend, e.detail === 0 ? 0 : 0.55); }));
      const target = snap(toMoney(toPos(tiers[Math.min(1, tiers.length - 1)].spend) + 0.035));
      meter.hidden = false;
      meter.closest('.lsl-spend')?.classList.add('has-meter');
      render(motionOn() ? 0 : target);
      meters.push({ meter, demo: () => {
        if (!G || !motionOn() || state.cart !== 0 || document.activeElement === field) return;
        const o = { c: 0 };
        state.tween = G.to(o, { c: target, duration: 1.8, ease: 'power2.inOut', delay: 0.2, onUpdate: () => render(snap(o.c)), onComplete: () => { state.tween = null; } });
      } });
    });
  };

  /* Spotlight border that follows the pointer on cards. */
  const initSpot = () => {
    if (!fine.matches) return;
    document.querySelectorAll('.lsl :is(.lsl-hl, .lsl-bundle, .lsl-ticket):not(.lsl-spot)').forEach((el) => el.classList.add('lsl-spot'));
    if (initSpot.bound) return;
    initSpot.bound = true;
    document.addEventListener('pointermove', (e) => {
      const el = e.target instanceof Element ? e.target.closest('.lsl-spot') : null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${e.clientX - r.left}px`);
      el.style.setProperty('--my', `${e.clientY - r.top}px`);
    }, { passive: true });
  };

  /* Rules accordion: GSAP height animation (native <details> without it). */
  const initRules = () => {
    if (initRules.bound) return;
    initRules.bound = true;
    document.addEventListener('click', (e) => {
      const summary = e.target.closest('.lsl-rule > summary');
      if (!summary || !G || !motionOn()) return;
      const details = summary.parentElement;
      const body = details.querySelector('.lsl-rule__body');
      if (!body) return;
      e.preventDefault();
      G.killTweensOf(body);
      if (details.open && !details.classList.contains('is-closing')) {
        details.classList.add('is-closing');
        G.fromTo(body, { height: body.offsetHeight, opacity: 1 }, { height: 0, opacity: 0, duration: 0.32, ease: 'power3.inOut', onComplete: () => { details.open = false; details.classList.remove('is-closing'); G.set(body, { clearProps: 'height,opacity,overflow' }); ST?.refresh(); } });
        G.set(body, { overflow: 'hidden' });
      } else {
        details.classList.remove('is-closing');
        details.open = true;
        G.fromTo(body, { height: 0, opacity: 0, overflow: 'hidden' }, { height: body.scrollHeight, opacity: 1, duration: 0.42, ease: 'power3.out', onComplete: () => { G.set(body, { clearProps: 'height,opacity,overflow' }); ST?.refresh(); } });
      }
    });
  };

  /* Hover card over mystery box "could contain" thumbs: one shared card, moved between thumbs. */
  let peek = null;
  const initPeek = () => {
    if (!document.querySelector('[data-lsl-peek]')) return;
    if (!peek) {
      const el = document.createElement('div');
      el.className = 'lsl-peek';
      el.id = 'lsl-peek';
      el.setAttribute('role', 'tooltip');
      el.innerHTML = '<span class="lsl-peek__arrow"></span><div class="lsl-peek__in"><span class="lsl-peek__img"><img alt="" decoding="async"></span><div class="lsl-peek__body"><small class="lsl-peek__vendor"></small><b class="lsl-peek__title"></b><p class="lsl-peek__rating"><span class="lsl-peek__stars"></span><span class="lsl-peek__count"></span></p><p class="lsl-peek__price"><strong></strong><s></s></p><span class="lsl-peek__view"></span></div></div>';
      document.body.appendChild(el);
      peek = { el, cur: null, timer: 0, on: false };
    }
    const P = peek;
    const q = (s) => P.el.querySelector(s);
    const gs = () => (G && motionOn() ? G : null);

    const fill = (a) => {
      const d = a.dataset;
      const clean = (v) => String(v || '').replace(/\s+/g, ' ').trim();
      const list = a.closest('[data-lsl-peek-list]')?.dataset || {};
      const img = q('.lsl-peek__img img');
      if (d.img) { img.src = d.img; img.hidden = false; } else { img.removeAttribute('src'); img.hidden = true; }
      q('.lsl-peek__vendor').textContent = d.vendor || '';
      q('.lsl-peek__title').textContent = d.title || '';
      const r = parseFloat(d.rating) || 0;
      const n = parseInt(d.count, 10) || 0;
      const rating = q('.lsl-peek__rating');
      rating.classList.toggle('is-empty', !n);
      q('.lsl-peek__stars').style.setProperty('--r', `${Math.max(0, Math.min(5, r)) * 20}%`);
      q('.lsl-peek__count').textContent = n
        ? `${r.toFixed(1)} · ${(list.labelReviews || '[count]').replace('[count]', n)}`
        : (list.labelNone || '');
      q('.lsl-peek__price strong').textContent = d.sold ? (list.labelSold || clean(d.price)) : clean(d.price);
      q('.lsl-peek__price').classList.toggle('is-sold', !!d.sold);
      q('.lsl-peek__price s').textContent = d.sold ? '' : clean(d.compare);
      q('.lsl-peek__view').textContent = list.labelView || '';
    };

    const place = (a) => {
      const r = a.getBoundingClientRect();
      const w = P.el.offsetWidth;
      const h = P.el.offsetHeight;
      const gap = 16;
      let below = r.top - h - gap < 8;
      const y = below ? r.bottom + gap : r.top - h - gap;
      const cx = r.left + r.width / 2;
      const x = Math.max(8, Math.min(window.innerWidth - w - 8, cx - w / 2));
      P.el.classList.toggle('is-below', below);
      P.el.style.setProperty('--ax', `${Math.round(cx - x)}px`);
      return { x: Math.round(x), y: Math.round(y), below };
    };

    const lift = (a, up) => {
      const g = gs();
      const items = [...(a.closest('[data-lsl-peek-list]')?.querySelectorAll('[data-lsl-peek]') || [])];
      if (g) {
        g.to(a, { y: up ? -4 : 0, scale: up ? 1.08 : 1, duration: up ? 0.35 : 0.3, ease: up ? 'back.out(2)' : 'power2.out', overwrite: 'auto' });
        items.forEach((o) => { if (o !== a) g.to(o, { opacity: up ? 0.45 : 1, duration: 0.25, overwrite: 'auto' }); });
      } else {
        a.classList.toggle('is-peek', up);
        items.forEach((o) => o.classList.toggle('is-dim', up && o !== a));
      }
    };

    const show = (a) => {
      clearTimeout(P.timer);
      if (P.cur === a && P.on) return;
      const prev = P.on ? P.cur : null;
      if (prev && prev !== a) lift(prev, false);
      P.cur = a;
      a.setAttribute('aria-describedby', 'lsl-peek');
      const g = gs();
      if (prev && g) {
        const body = [q('.lsl-peek__img'), ...q('.lsl-peek__body').children];
        g.to(body, { autoAlpha: 0, y: -4, duration: 0.12, ease: 'power1.in', overwrite: true, onComplete: () => {
          fill(a);
          const pos = place(a);
          g.to(P.el, { x: pos.x, y: pos.y, duration: 0.42, ease: 'power3.out', overwrite: 'auto' });
          g.fromTo(body, { autoAlpha: 0, y: 6 }, { autoAlpha: 1, y: 0, duration: 0.3, stagger: 0.03, ease: 'power2.out' });
        } });
      } else {
        fill(a);
        const pos = place(a);
        if (g) {
          const body = [q('.lsl-peek__img'), ...q('.lsl-peek__body').children];
          g.killTweensOf([P.el, ...body]);
          g.set(P.el, { x: pos.x, y: pos.y });
          g.fromTo(P.el, { autoAlpha: 0, scale: 0.9, rotation: pos.below ? 2 : -2, yPercent: pos.below ? -6 : 6, transformOrigin: `${P.el.style.getPropertyValue('--ax')} ${pos.below ? '0%' : '100%'}` },
            { autoAlpha: 1, scale: 1, rotation: 0, yPercent: 0, duration: 0.45, ease: 'back.out(1.6)' });
          g.fromTo(q('.lsl-peek__img img'), { scale: 1.18 }, { scale: 1, duration: 0.6, ease: 'power3.out' });
          g.fromTo(q('.lsl-peek__body').children, { autoAlpha: 0, y: 6 }, { autoAlpha: 1, y: 0, duration: 0.32, stagger: 0.04, delay: 0.06, ease: 'power2.out' });
        } else {
          P.el.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
          P.el.classList.add('is-on');
        }
      }
      P.on = true;
      lift(a, true);
    };

    const hide = (now) => {
      clearTimeout(P.timer);
      const run = () => {
        if (!P.on) return;
        const a = P.cur;
        P.on = false;
        if (a) { lift(a, false); a.removeAttribute('aria-describedby'); }
        const g = gs();
        if (g) g.to(P.el, { autoAlpha: 0, scale: 0.95, yPercent: P.el.classList.contains('is-below') ? -3 : 3, duration: 0.18, ease: 'power2.in', overwrite: true });
        else P.el.classList.remove('is-on');
      };
      if (now) run(); else P.timer = setTimeout(run, 90);
    };

    document.querySelectorAll('[data-lsl-peek]').forEach((a) => {
      if (a.dataset.lslPeekBound) return;
      a.dataset.lslPeekBound = '1';
      a.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && fine.matches) show(a); });
      a.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hide(); });
      a.addEventListener('focus', () => { if (a.matches(':focus-visible')) show(a); });
      a.addEventListener('blur', () => hide());
    });
    if (!P.bound) {
      P.bound = true;
      window.addEventListener('scroll', () => hide(true), { passive: true });
      window.addEventListener('resize', () => hide(true));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(true); });
    }
  };

  /* GSAP + ScrollTrigger, loaded only when motion is on. */
  const loadScript = (src) => new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = src;
    tag.onload = resolve;
    tag.onerror = reject;
    document.head.appendChild(tag);
  });
  const loadMotion = async () => {
    const c = config();
    if (!window.gsap) {
      if (window.lsLoadGsap) await window.lsLoadGsap(c.lslGsap);
      else await loadScript(c.lslGsap);
    }
    if (!window.ScrollTrigger) await loadScript(c.lslSt);
    window.gsap.registerPlugin(window.ScrollTrigger);
    return [window.gsap, window.ScrollTrigger];
  };

  const below = (el) => el.getBoundingClientRect().top > window.innerHeight * 0.92;
  const onReveal = (trigger, play, start = 'top 86%') => ST.create({ trigger, start, end: 'max', once: true, onEnter: play });

  /* Numbers in headings: the display face's tight tracking jams narrow figures ("11.11"), so digit runs get
     their own span (.lsl-num) with looser spacing. Runs on every sale heading, motion or not. */
  const NUM = /(\d[\d.,:/]*\d|\d)/;
  const numFrag = (text) => {
    const frag = document.createDocumentFragment();
    text.split(NUM).forEach((part, i) => {
      if (!part) return;
      if (i % 2) { const n = document.createElement('span'); n.className = 'lsl-num'; n.textContent = part; frag.appendChild(n); }
      else frag.appendChild(document.createTextNode(part));
    });
    return frag;
  };
  const spaceNumbers = () => {
    document.querySelectorAll('.lsl :is(h1, h2, h3)').forEach((h) => {
      if (h.dataset.lslNum) return;
      h.dataset.lslNum = '1';
      /* also inside spotlight words, so "11.11" in the gradient keeps its dot visible */
      const nodes = [...h.childNodes, ...[...h.querySelectorAll(':scope > .lsl-key')].flatMap((k) => [...k.childNodes])];
      nodes.forEach((node) => {
        if (node.nodeType === 3 && NUM.test(node.textContent)) node.replaceWith(numFrag(node.textContent));
      });
    });
  };

  /* Spotlight words (Sale: colour scheme > Spotlight words): each match in a section heading is
     wrapped in .lsl-key, which the colour section paints with the spotlight gradient */
  const initKeys = () => {
    const list = (config().lslKeys || '').split(',').map((w) => w.trim()).filter(Boolean);
    if (!list.length) return;
    const alt = list.sort((a, b) => b.length - a.length).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const rx = new RegExp(`(^|\\s)(${alt})(?=$|\\s|[.,!?:;])`, 'gi');
    document.querySelectorAll('.lsl .lsl-h2:not([data-lsl-keys])').forEach((h) => {
      h.dataset.lslKeys = '';
      [...h.childNodes].forEach((node) => {
        if (node.nodeType !== 3) return;
        const s = node.textContent;
        let last = 0;
        const frag = document.createDocumentFragment();
        s.replace(rx, (m, pre, word, i) => {
          const at = i + pre.length;
          frag.append(s.slice(last, at));
          const k = document.createElement('span');
          k.className = 'lsl-key';
          k.textContent = word;
          frag.append(k);
          last = at + word.length;
          return m;
        });
        if (!last) return;
        frag.append(s.slice(last));
        node.replaceWith(frag);
      });
    });
  };

  const splitWords = (el) => {
    if (el.dataset.lslSplit === 'done') return [];
    el.dataset.lslSplit = 'done';
    /* fold number spans back into text first; they are rebuilt inside each word */
    el.querySelectorAll(':scope > .lsl-num').forEach((n) => n.replaceWith(document.createTextNode(n.textContent)));
    el.normalize();
    const words = [];
    [...el.childNodes].forEach((node) => {
      if (node.nodeType === 1 && node.classList.contains('lsl-key')) {
        const outer = document.createElement('span');
        outer.className = 'lsl-w';
        const inner = document.createElement('span');
        node.before(outer);
        inner.appendChild(node);
        outer.appendChild(inner);
        words.push(inner);
        return;
      }
      if (node.nodeType !== 3) return;
      const frag = document.createDocumentFragment();
      /* ordinary whitespace only: a no-break space keeps a name like "ZiiGaat x B_Media" in one word */
      node.textContent.split(/([ \t\n\r]+)/).forEach((part) => {
        if (!part) return;
        if (/^[ \t\n\r]+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
        const outer = document.createElement('span');
        outer.className = 'lsl-w';
        const inner = document.createElement('span');
        inner.appendChild(numFrag(part));
        outer.appendChild(inner);
        frag.appendChild(outer);
        words.push(inner);
      });
      node.replaceWith(frag);
    });
    return words;
  };

  const initMotion = () => {
    const gsap = G;
    const mm = gsap.matchMedia();

    /* Hero entrance + pointer depth + float */
    document.querySelectorAll('.lsl-hero[data-lsl-hero]').forEach((hero) => {
      const ins = [...hero.querySelectorAll('[data-lsl-in]')];
      const kv = hero.querySelector('.lsl-hero__kv, .lsl-hero__title');
      const cards = [...hero.querySelectorAll('.lsl-art-card')];
      const bgImg = hero.querySelector('[data-lsl-bg] img');
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      if (bgImg) tl.fromTo(bgImg, { scale: 1.12 }, { scale: 1, duration: 1.8, ease: 'power2.out' }, 0);
      tl.fromTo(hero.querySelector('.lsl-hero__glow'), { opacity: 0 }, { opacity: 1, duration: 1.4 }, 0);
      if (kv) tl.fromTo(kv, { opacity: 0, y: 34, scale: 0.94 }, { opacity: 1, y: 0, scale: 1, duration: 1.1 }, 0.1);
      cards.forEach((card, i) => {
        const left = card.closest('.lsl-hero__side--a');
        tl.fromTo(card, { opacity: 0, x: left ? -70 : 70, y: 40, rotation: left ? -6 : 6 }, { opacity: 1, x: 0, y: 0, rotation: 0, duration: 1.1 }, 0.3 + i * 0.08);
      });
      const rest = ins.filter((el) => el !== kv && !cards.includes(el));
      if (rest.length) tl.fromTo(rest, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.8, stagger: 0.06 }, 0.45);
      tl.add(() => {
        /* no idle float: the cards only follow the pointer (below) and lift on hover (ls-sale.css) */
        mm.add('(hover: hover) and (pointer: fine)', () => {
          const layers = [...hero.querySelectorAll('[data-lsl-depth]')].map((el) => ({
            el,
            d: parseFloat(el.dataset.lslDepth) || 1,
            x: gsap.quickTo(el, 'x', { duration: 0.9, ease: 'power3' }),
            y: gsap.quickTo(el, 'y', { duration: 0.9, ease: 'power3' }),
          }));
          const move = (e) => {
            const r = hero.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width - 0.5;
            const py = (e.clientY - r.top) / r.height - 0.5;
            layers.forEach((l) => { l.x(px * 26 * l.d); l.y(py * 18 * l.d); });
          };
          const leave = () => layers.forEach((l) => { l.x(0); l.y(0); });
          hero.addEventListener('pointermove', move);
          hero.addEventListener('pointerleave', leave);
          return () => { hero.removeEventListener('pointermove', move); hero.removeEventListener('pointerleave', leave); };
        });
      });
      const bg = hero.querySelector('[data-lsl-bg]');
      if (bg) gsap.to(bg, { yPercent: 14, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
      const stage = hero.querySelector('.lsl-hero__art-inner');
      if (stage) gsap.to(stage, { y: -60, opacity: 0.35, ease: 'none', scrollTrigger: { trigger: hero, start: 'center top+=35%', end: 'bottom top', scrub: true } });
    });

    /* Headline word reveals (split layout hero title + every section heading) */
    document.querySelectorAll('.lsl .lsl-h2, .lsl [data-lsl-split]').forEach((h) => {
      if (h.closest('.lsl-hero--artwork')) return;
      const inHero = !!h.closest('.lsl-hero');
      if (!inHero && !below(h)) return;
      /* a heading painted with background-clip: text (spotlight sections, festival style) goes blank
         once its words are transformed, so it rises in whole instead */
      const hcs = getComputedStyle(h);
      if (hcs.backgroundClip === 'text' || hcs.webkitBackgroundClip === 'text') {
        gsap.set(h, { opacity: 0, y: 30 });
        onReveal(h, () => gsap.to(h, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', clearProps: 'transform' }));
        return;
      }
      const words = splitWords(h);
      if (!words.length) return;
      gsap.set(words, { yPercent: 110 });
      if (inHero) gsap.to(words, { yPercent: 0, duration: 1, ease: 'power4.out', stagger: 0.05, delay: 0.15 });
      else onReveal(h, () => gsap.to(words, { yPercent: 0, duration: 0.95, ease: 'power4.out', stagger: 0.045 }));
    });

    /* Copy blocks: stagger their parts in */
    document.querySelectorAll('.lsl [data-lsl-reveal]').forEach((block) => {
      if (!below(block)) return;
      const parts = [...block.querySelectorAll(':scope > :not(h2):not(ol), :scope > ol > li')];
      gsap.set(parts, { opacity: 0, y: 22 });
      onReveal(block, () => gsap.to(parts, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.06, delay: 0.15 }));
    });

    /* Cards and rows: batched stagger */
    const batchSel = '.lsl :is(.lsl-hl, .lsl-ticket, .lsl-bundle, .lsl-grid .lsl-item, .lsl-rule, .lsl-meter, .lsl-news-row, .lsl-newsletter__card, .lsl-event__form, .lsl-event__side > .lsl-award, .lsl-clock, .lsl-products__foot, .lsl-lucky__card)';
    const items = [...document.querySelectorAll(batchSel)].filter((el) => !el.hidden && below(el));
    if (items.length) {
      gsap.set(items, { opacity: 0, y: 30 });
      ST.batch(items, { start: 'top 92%', end: 'max', once: true, onEnter: (b) => gsap.to(b, { opacity: 1, y: 0, duration: 0.75, ease: 'power3.out', stagger: 0.07, overwrite: true }) });
    }

    /* Bundles: the two products slide together, the plus pops in, badges follow */
    document.querySelectorAll('.lsl [data-lsl-bundle]').forEach((card) => {
      if (!below(card)) return;
      const a = card.querySelector('.lsl-bundle__img--a');
      const b = card.querySelector('.lsl-bundle__img--b');
      const plus = card.querySelector('.lsl-bundle__plus');
      const tags = card.querySelectorAll('.lsl-bundle__letter, .lsl-bundle__saving');
      if (a && b) gsap.set([a, b], { x: (i) => (i ? 48 : -48), opacity: 0 });
      if (plus) gsap.set(plus, { scale: 0, rotation: -90 });
      if (tags.length) gsap.set(tags, { opacity: 0, y: -8 });
      onReveal(card, () => {
        const tl = gsap.timeline({ delay: 0.15, defaults: { ease: 'power3.out' } });
        if (a && b) tl.to([a, b], { x: 0, opacity: 1, duration: 0.9, clearProps: 'transform' }, 0);
        if (plus) tl.to(plus, { scale: 1, rotation: 0, duration: 0.6, ease: 'back.out(2)', clearProps: 'transform' }, 0.35);
        if (tags.length) tl.to(tags, { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, clearProps: 'transform' }, 0.5);
      }, 'top 90%');
    });

    /* Floating art: scroll parallax and the glass sticker easing in */
    document.querySelectorAll('.lsl [data-lsl-parallax]').forEach((el) => {
      gsap.fromTo(el, { y: 36 }, { y: -36, ease: 'none', scrollTrigger: { trigger: el.closest('.lsl') || el, start: 'top bottom', end: 'bottom top', scrub: 0.6 } });
    });
    document.querySelectorAll('.lsl-event__stage .lsl-award').forEach((el) => {
      if (!below(el)) return;
      gsap.set(el, { opacity: 0, y: 30, scale: 0.96 });
      onReveal(el, () => gsap.to(el, { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'back.out(1.4)', delay: 0.35 }), 'top 92%');
    });

    /* Chapter marquee: constant drift, faster while scrolling */
    document.querySelectorAll('[data-lsl-marquee]').forEach((m) => {
      const track = m.querySelector('.lsl-marquee__track');
      const groupW = track.firstElementChild?.offsetWidth || 1000;
      const reverse = m.dataset.direction === 'right';
      const tween = gsap.fromTo(track, { x: reverse ? -groupW : 0 }, { x: reverse ? 0 : -groupW, duration: groupW / 70, ease: 'none', repeat: -1, paused: true });
      let settle = null;
      ST.create({
        trigger: m, start: 'top bottom', end: 'bottom top',
        onToggle: (self) => (self.isActive ? tween.play() : tween.pause()),
        onUpdate: (self) => {
          const boost = Math.min(5, 1 + Math.abs(self.getVelocity()) / 350);
          gsap.to(tween, { timeScale: boost, duration: 0.2, overwrite: true });
          settle?.kill();
          settle = gsap.delayedCall(0.18, () => gsap.to(tween, { timeScale: 1, duration: 0.9, ease: 'power2.out', overwrite: true }));
        },
      });
    });

    /* Reservation art: float, tilt with the pointer, foil sweep */
    document.querySelectorAll('[data-lsl-tilt]').forEach((wrap) => {
      const card = wrap.querySelector('.lsl-res__card');
      const foil = wrap.querySelector('.lsl-res__foil');
      if (!card) return;
      gsap.to(card, { y: -12, duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1 });
      if (foil) gsap.fromTo(foil, { '--fx': '120%' }, { '--fx': '-20%', duration: 2.4, ease: 'power2.inOut', repeat: -1, repeatDelay: 2.6 });
      mm.add('(hover: hover) and (pointer: fine)', () => {
        const rx = gsap.quickTo(card, 'rotationX', { duration: 0.7, ease: 'power3' });
        const ry = gsap.quickTo(card, 'rotationY', { duration: 0.7, ease: 'power3' });
        const move = (e) => {
          const r = wrap.getBoundingClientRect();
          const px = (e.clientX - r.left) / r.width - 0.5;
          const py = (e.clientY - r.top) / r.height - 0.5;
          rx(-py * 16); ry(px * 20);
          if (foil) gsap.to(foil, { '--fx': `${(0.5 - px) * 140 + 50}%`, duration: 0.4, overwrite: true });
        };
        const leave = () => { rx(0); ry(0); };
        wrap.addEventListener('pointermove', move);
        wrap.addEventListener('pointerleave', leave);
        return () => { wrap.removeEventListener('pointermove', move); wrap.removeEventListener('pointerleave', leave); };
      });
    });

    /* Magnetic primary buttons */
    mm.add('(hover: hover) and (pointer: fine)', () => {
      const offs = [];
      document.querySelectorAll('.lsl [data-lsl-magnet]').forEach((btn) => {
        const x = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3' });
        const y = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3' });
        const move = (e) => {
          const r = btn.getBoundingClientRect();
          x(Math.max(-8, Math.min(8, (e.clientX - r.left - r.width / 2) * 0.22)));
          y(Math.max(-6, Math.min(6, (e.clientY - r.top - r.height / 2) * 0.3)));
        };
        const leave = () => { x(0); y(0); };
        btn.addEventListener('pointermove', move);
        btn.addEventListener('pointerleave', leave);
        offs.push(() => { btn.removeEventListener('pointermove', move); btn.removeEventListener('pointerleave', leave); });
      });
      return () => offs.forEach((f) => f());
    });

    /* Savings meter demo once it comes into view */
    meters.forEach(({ meter, demo }) => onReveal(meter, demo, 'top 80%'));

    document.querySelectorAll('[data-lsl-nav]').forEach((nav) => nav._lslUpdate?.());
    window.addEventListener('load', () => ST.refresh(), { once: true });
    if (document.fonts?.ready) document.fonts.ready.then(() => ST.refresh());
  };

  const DAY = 86400000;
  const dUTC = (v) => { const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(v || '').trim()); return m ? Date.UTC(+m[1], m[2] - 1, +m[3]) : null; };
  const saleToday = () => dUTC(new Date(Date.now() + tzMinutes() * 60000).toISOString());
  const make = (tag, cls, txt) => { const n = document.createElement(tag); if (cls) n.className = cls; if (txt != null) n.textContent = txt; return n; };

  /* Sale planner (ls-sale-planner), after EPOMAKER's 11.11 guide: a popup with two tools.
     Saving: a budget slider unlocks offer rows, each with its own value at that budget (never a total).
     Key dates: the days something opens, what opens, and an .ics download. */
  const initPlanner = () => {
    document.querySelectorAll('[data-lsl-plan]:not([data-lsl-ready])').forEach((root) => {
      root.dataset.lslReady = '';
      let data;
      try { data = JSON.parse(root.querySelector('[data-plan-data]').textContent); } catch (e) { return; }
      const t = data.t || {};
      const items = data.items || {};
      const dlg = root.querySelector('[data-plan-dialog]');
      const launch = root.querySelector('[data-plan-launch]');
      if (!dlg) return;
      const today = saleToday();
      /* the last day is inclusive; with a time (2026-10-30 11:00, GMT+8 like the rest) it ends then */
      const endsAt = (v) => {
        const d = dUTC(v);
        if (d == null) return null;
        const m = /\s(\d{1,2}):(\d{2})/.exec(v || '');
        return d - tzMinutes() * 60000 + (m ? (+m[1] * 60 + +m[2]) * 60000 : DAY);
      };
      const isEnded = (k) => items[k] && endsAt(items[k].to) != null && Date.now() >= endsAt(items[k].to);
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const rich = (s) => esc(s).replace(/&lt;(\/?)b&gt;/g, '<$1b>');
      const fill = (s, o) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in o ? o[k] : m));
      const num = (v, d) => { const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.]/g, '')); return Number.isNaN(n) ? d : n; };
      const usd = (n) => '$' + (Math.round(n * 100) % 100 ? n.toFixed(2) : String(Math.round(n)));
      const tiers = String(data.tiers || '').split(',').map((p) => p.split(':').map((x) => num(x, NaN)))
        .filter((p) => p.length === 2 && !Number.isNaN(p[0]) && !Number.isNaN(p[1]))
        .map(([min, save]) => ({ min, save })).sort((a, b) => a.min - b.min);
      const rcMins = String(data.rcMins || '').split(',').map((x) => num(x, NaN)).filter((x) => !Number.isNaN(x)).sort((a, b) => a - b);
      const cb = { min: num(data.cbMin, 500), pct: num(data.cbPct, 10), cap: num(data.cbCap, 100) };

      /* rows */
      const ICON_ON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
      const ICON_OFF = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
      const makeRow = (k, list) => {
        const it = items[k];
        if (!it) return null;
        const li = make('li', 'lsl-plan__row');
        li.innerHTML = '<span class="lsl-plan__row-ic"></span><div><p class="lsl-plan__row-t"></p><p class="lsl-plan__row-v"></p></div>';
        const tt = li.querySelector('.lsl-plan__row-t');
        tt.textContent = it.title;
        if (it.dates) tt.append(make('small', '', it.dates));
        if (it.link) { const a = make('a', 'lsl-plan__go', t.go || 'Go'); a.href = it.link; li.append(a); }
        li._k = k;
        list.append(li);
        return li;
      };
      const setRow = (li, state, html) => {
        if (isEnded(li._k)) { state = 'ended'; html = esc(t.ended); }
        if (li._s !== state) {
          li._s = state;
          li.classList.toggle('is-on', state === 'on');
          li.classList.toggle('is-ended', state === 'ended');
          li.querySelector('.lsl-plan__row-ic').innerHTML = state === 'on' ? ICON_ON : ICON_OFF;
        }
        if (li._h !== html) { li._h = html; li.querySelector('.lsl-plan__row-v').innerHTML = html; }
      };
      const spendText = (A) => {
        let cur = null;
        let next = null;
        tiers.forEach((x) => { if (A >= x.min) cur = x; else if (!next) next = x; });
        if (!cur) return next ? rich(fill(t.spendGap, { gap: usd(next.min - A), save: usd(next.save) })) : '';
        let s = fill(t.spend, { save: usd(cur.save) });
        if (next) s = `${s.trim()} ${fill(t.spendNext, { gap: usd(next.min - A), save: usd(next.save) }).trim()}`; /* the editor strips the default's leading space */
        return rich(s);
      };
      const rcText = (A) => {
        let n = 0;
        rcMins.forEach((m, i) => { if (A >= m) n = i + 1; });
        return rich(t['rc' + Math.min(n, 3)] || '');
      };

      /* spotlight: ticked activities that are open right now, above both tools */
      const spotBox = root.querySelector('[data-plan-spot]');
      if (spotBox) {
        const opensAt = (v) => {
          const d = dUTC(v);
          if (d == null) return null;
          const m = /\s(\d{1,2}):(\d{2})/.exec(v || '');
          return d - tzMinutes() * 60000 + (m ? (+m[1] * 60 + +m[2]) * 60000 : 0);
        };
        const endsOn = (v) => { const d = new Date(dUTC(v)); return d.getUTCDate() + ' ' + new Intl.DateTimeFormat(document.documentElement.lang || 'en', { timeZone: 'UTC', month: 'short' }).format(d); }; /* 29 Oct, as the dates are written */
        const now = Date.now();
        Object.keys(items).filter((k) => items[k].spot && !isEnded(k) && !(opensAt(items[k].from) > now)).forEach((k) => {
          const it = items[k];
          const card = make(it.link ? 'a' : 'div', 'lsl-plan__spot-card');
          if (it.link) card.href = it.link;
          const top = make('p', 'lsl-plan__spot-top');
          top.append(make('span', 'lsl-plan__spot-badge', t.spot || 'Open now'));
          if (it.to && t.spotEnds) top.append(make('span', 'lsl-plan__spot-end', fill(t.spotEnds, { date: endsOn(it.to) })));
          card.append(top, make('p', 'lsl-plan__spot-t', it.title));
          if (it.why) card.append(make('p', 'lsl-plan__spot-v', it.why));
          if (it.link && t.spotGo) card.append(make('span', 'lsl-plan__spot-go', t.spotGo));
          spotBox.append(card);
        });
        spotBox.hidden = !spotBox.childElementCount;
        spotBox.classList.toggle('is-one', spotBox.childElementCount === 1);
      }

      /* tool 1: saving */
      const savePanel = root.querySelector('[data-plan-panel="save"]');
      const range = savePanel.querySelector('[data-plan-range]');
      const out = savePanel.querySelector('[data-plan-amt]');
      const list = savePanel.querySelector('[data-plan-rows]');
      const tickBox = savePanel.querySelector('[data-plan-ticks]');
      const max = num(data.max, 1000);
      const ROWS = [
        { k: 'deals', need: 0, v: () => rich(t.deals) },
        { k: 'mystery', need: num(data.mysteryFrom, 26), v: () => rich(t.mystery) },
        { k: 'bundles', need: num(data.bundlesFrom, 27), v: () => rich(t.bundles) },
        { k: 'rc', need: rcMins[0] || 80, v: rcText },
        { k: 'spend', need: tiers.length ? tiers[0].min : 150, v: spendText },
        { k: 'cashback', need: cb.min, v: (A) => rich(fill(t.cashback, { back: usd(Math.min(A * cb.pct / 100, cb.cap)) })) },
      ].filter((r) => items[r.k]).sort((a, b) => a.need - b.need);
      ROWS.forEach((r) => { r.li = makeRow(r.k, list); });
      const marks = [...new Set([...tiers.map((x) => x.min), cb.min])].filter((m) => m > 0 && m <= max).sort((a, b) => a - b);
      const ticks = marks.map((m) => { const s = make('span', 'lsl-plan__tick', usd(m)); s.style.left = (m / max * 100) + '%'; tickBox.append(s); return { m, s }; });
      let A = -1;
      const set = (v) => {
        v = Math.max(0, Math.min(max, Math.round(v / 10) * 10));
        if (v === A) return;
        A = v;
        if (Number(range.value) !== v) range.value = v;
        range.style.setProperty('--p', (v / max * 100) + '%');
        if (v >= max && t.atMax && t.atMax.includes('[amount]')) {
          const [pre, post] = t.atMax.split('[amount]');
          out.innerHTML = (pre.trim() ? `<small>${esc(pre.trim())}</small> ` : '') + esc(usd(v)) + (post.trim() ? `<small>${esc(post.trim())}</small>` : '');
        } else out.textContent = usd(v);
        ticks.forEach((x) => x.s.classList.toggle('is-on', v >= x.m));
        ROWS.forEach((r) => {
          if (!r.li) return;
          const on = v >= r.need;
          setRow(r.li, on ? 'on' : 'off', on || r.k === 'spend' || r.k === 'rc' ? r.v(v) : esc(fill(t.locked, { amount: usd(r.need) })));
        });
      };
      let sweepRaf = 0;
      range.addEventListener('input', () => { cancelAnimationFrame(sweepRaf); set(Number(range.value)); });
      set(0);
      let swept = false;
      const sweep = () => {
        if (swept) return;
        swept = true;
        const to = Math.min(max, num(data.start, 300));
        if (reduced.matches) { set(to); return; }
        const t0 = performance.now();
        const step = (now) => {
          const k = Math.min(1, (now - t0) / 1400);
          set(to * (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2));
          if (k < 1) sweepRaf = requestAnimationFrame(step);
        };
        sweepRaf = requestAnimationFrame(step);
      };

      /* tool 2: key dates */
      const datePanel = root.querySelector('[data-plan-panel="date"]');
      const box = datePanel.querySelector('[data-plan-days]');
      const dayOut = datePanel.querySelector('[data-plan-day]');
      const days = new Map();
      const add = (d, k, kind) => { if (d == null) return; if (!days.has(d)) days.set(d, { d, opens: [], ends: [] }); days.get(d)[kind].push(k); };
      Object.keys(items).forEach((k) => add(dUTC(items[k].from), k, 'opens'));
      const fin = dUTC(data.final);
      if (fin != null) Object.keys(items).forEach((k) => { if (dUTC(items[k].to) === fin) add(fin, k, 'ends'); });
      const dayList = [...days.values()].sort((a, b) => a.d - b.d);
      const fmtD = (d, o) => new Intl.DateTimeFormat(document.documentElement.lang || 'en', { timeZone: 'UTC', ...o }).format(d);
      /* The reminder is for the day's first opening. An item's first day may carry a time
         (2026-10-26 10:00, in the sale's time zone); a day whose items have no time starts at 00:00. */
      const icsFor = (x) => {
        const at = x.opens.map((k) => /\s(\d{1,2}):(\d{2})/.exec(items[k].from || '')).filter(Boolean).map((m) => +m[1] * 60 + +m[2]);
        const start = x.d - tzMinutes() * 60000 + (at.length ? Math.min(...at) : 0) * 60000;
        const names = x.opens.map((k) => items[k].title).join(', ');
        const what = [...x.opens, ...x.ends].map((k) => items[k].title + ' (' + (items[k].dates || '') + ')').join('\n');
        return { title: fill(t.icsTitle, { title: names || t.dayFinal }), start, end: start + 3600000, details: what, url: location.href.split('#')[0], alarm: 0, file: (t.icsFile || 'sale') + '-' + new Date(x.d).toISOString().slice(0, 10) };
      };
      const showDay = (x) => {
        box.querySelectorAll('.lsl-plan__dt').forEach((b) => b.setAttribute('aria-pressed', String(b === x.b)));
        dayOut.replaceChildren();
        const head = make('p', 'lsl-plan__day-h');
        const diff = Math.round((x.d - today) / DAY);
        head.textContent = fmtD(x.d, { weekday: 'long', day: 'numeric', month: 'long' });
        head.append(make('small', '', diff === 0 ? t.isToday : diff > 0 ? fill(t.inDays, { n: diff }) : t.past));
        dayOut.append(head);
        [['opens', t.opens], ['ends', t.ends]].forEach(([kind, label]) => {
          if (!x[kind].length) return;
          dayOut.append(make('p', 'lsl-plan__day-k', label));
          const ul = make('ul', 'lsl-plan__rows');
          x[kind].forEach((k) => {
            const li = makeRow(k, ul);
            if (li) setRow(li, 'on', esc(items[k].why || items[k].dates));
          });
          dayOut.append(ul);
        });
        if (x.d >= today) {
          const c = make('button', 'lsl-btn lsl-btn--ghost lsl-plan__ics', t.ics);
          c.type = 'button';
          c.addEventListener('click', () => cal.menu(c, icsFor(x)));
          dayOut.append(c);
        }
      };
      dayList.forEach((x) => {
        const b = make('button', 'lsl-plan__dt');
        b.type = 'button';
        b.setAttribute('aria-pressed', 'false');
        const n = x.opens.length;
        const cap = x.d === fin && !n ? t.dayFinal : n === 1 ? t.dayOne : fill(t.dayCount, { count: n });
        b.append(make('span', 'lsl-plan__dt-w', fmtD(x.d, { weekday: 'short' })), make('span', 'lsl-plan__dt-d', fmtD(x.d, { day: 'numeric' })), make('span', 'lsl-plan__dt-m', fmtD(x.d, { month: 'short' })), make('span', 'lsl-plan__dt-c', cap));
        if (x.d < today) b.classList.add('is-past');
        b.addEventListener('click', () => showDay(x));
        x.b = b;
        box.append(b);
      });
      const firstDay = dayList.find((x) => x.d >= today) || dayList[dayList.length - 1];

      /* tabs */
      const tabs = [...root.querySelectorAll('[data-plan-tab]')];
      const choose = (mode, focus) => {
        tabs.forEach((b) => { const on = b.dataset.planTab === mode; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; if (on && focus) b.focus(); });
        root.querySelectorAll('[data-plan-panel]').forEach((p) => { p.hidden = p.dataset.planPanel !== mode; });
        if (mode === 'save') sweep();
        if (mode === 'date' && firstDay && !dayOut.childElementCount) showDay(firstDay);
      };
      tabs.forEach((b, i) => {
        b.addEventListener('click', () => choose(b.dataset.planTab));
        b.addEventListener('keydown', (e) => {
          const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (d) { e.preventDefault(); choose(tabs[(i + d + tabs.length) % tabs.length].dataset.planTab, true); }
        });
      });

      /* popup */
      let place = () => {};
      const open = () => {
        if (typeof dlg.showModal !== 'function') return;
        dlg.showModal();
        document.documentElement.classList.add('lsl-lock');
        choose(tabs.find((b) => b.getAttribute('aria-selected') === 'true')?.dataset.planTab || 'save');
      };
      dlg.addEventListener('close', () => { document.documentElement.classList.remove('lsl-lock'); place(); });
      dlg.addEventListener('click', (e) => {
        if (e.target === dlg || e.target.closest('[data-plan-close]')) { dlg.close(); return; }
        if (e.target.closest('.lsl-plan__go, a.lsl-plan__spot-card')) dlg.close();
      });
      launch?.addEventListener('click', open);
      document.querySelectorAll('a[href="#' + root.id + '"]').forEach((a) => a.addEventListener('click', (e) => { e.preventDefault(); open(); }));
      if (!launch) return;
      (document.getElementById('MainContent') || document.body).append(launch);
      let ticking = false;
      /* data-plan-show (homepage copy): the button shows once the visitor has scrolled that % of a screen */
      const showAt = root.dataset.planShow;
      place = () => { ticking = false; launch.hidden = (showAt != null ? scrollY < innerHeight * Number(showAt) / 100 : root.getBoundingClientRect().top > 0) || dlg.open; };
      addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(place); } }, { passive: true });
      place();
    });
  };

  /* Timeline, cards style: key-date flags, phase filters and a row that keeps drifting (clones wrap it;
     slows to a stop under the mouse, on focus and after a touch; only on screen; never under reduced motion). */
  const initTimelineCards = () => {
    document.querySelectorAll('[data-lsl-tl]:not([data-lsl-tlc])').forEach((root) => {
      const row = root.querySelector('[data-tl-cards]');
      if (!row) return;
      root.dataset.lslTlc = '';
      const cards = [...row.querySelectorAll('.lsl-tlc__card')];
      const datesEl = root.querySelector('.lsl-tlc__dates');
      const paintDates = () => {
        if (!datesEl) return;
        const day = Math.floor((Date.now() + tzMinutes() * 60000) / DAY) * DAY;
        let nextSet = false;
        datesEl.querySelectorAll('[data-date]').forEach((d) => {
          const v = dUTC(d.dataset.date);
          const isToday = v === day;
          const next = !nextSet && v > day;
          if (next) nextSet = true;
          d.classList.toggle('is-today', isToday);
          d.classList.toggle('is-past', v < day);
          d.classList.toggle('is-next', next);
          const f = isToday ? datesEl.dataset.flagToday : next ? datesEl.dataset.flagNext : '';
          if (f) d.dataset.flag = f; else d.removeAttribute('data-flag');
        });
      };
      paintDates();
      setInterval(paintDates, 60000);

      let rebuild = () => {};
      const speed = Number(row.dataset.tlLoop) || 0;
      if (speed && cards.length && !reduced.matches && !window.Shopify?.designMode) {
        let loopW = 0; let pos = 0; let set = -1; let v = 0; let last = 0; let raf = 0;
        let onScreen = false; let hover = false; let focus = false; let holdUntil = 0;
        rebuild = () => {
          row.querySelectorAll('[data-tl-clone]').forEach((c) => c.remove());
          row.classList.remove('is-looping');
          loopW = 0;
          const vis = cards.filter((c) => !c.hidden);
          if (!vis.length || row.scrollWidth <= row.clientWidth + 2) { row.scrollLeft = 0; pos = 0; return; }
          const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
          const lastCard = vis[vis.length - 1];
          loopW = lastCard.offsetLeft + lastCard.offsetWidth + gap - vis[0].offsetLeft;
          let filled = 0;
          while (filled < row.clientWidth + loopW * 0.1) {
            vis.forEach((c) => {
              const k = c.cloneNode(true);
              k.dataset.tlClone = '';
              k.setAttribute('aria-hidden', 'true');
              k.querySelectorAll('a, button').forEach((a) => a.setAttribute('tabindex', '-1'));
              row.append(k);
            });
            filled += loopW;
          }
          row.classList.add('is-looping');
          pos = row.scrollLeft % loopW;
          set = -1;
        };
        const step = (now) => {
          raf = 0;
          const dt = Math.min(0.05, (now - (last || now)) / 1000);
          last = now;
          const want = hover || focus || now < holdUntil || document.hidden ? 0 : speed;
          v += (want - v) * (1 - Math.exp(-dt / 0.45));
          if (loopW) {
            if (set !== -1 && Math.abs(row.scrollLeft - set) > 2) pos = row.scrollLeft;
            pos += v * dt;
            if (pos >= loopW) pos -= loopW; else if (pos < 0) pos += loopW;
            row.scrollLeft = pos;
            set = row.scrollLeft;
          }
          if (onScreen) raf = requestAnimationFrame(step);
        };
        const run = () => { if (!raf && onScreen) { last = 0; raf = requestAnimationFrame(step); } };
        new IntersectionObserver((es) => { onScreen = es[0].isIntersecting; if (onScreen) run(); }, { rootMargin: '100px' }).observe(row);
        row.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hover = true; });
        row.addEventListener('pointerleave', () => { hover = false; });
        row.addEventListener('focusin', () => { focus = true; });
        row.addEventListener('focusout', () => { setTimeout(() => { focus = row.matches(':focus-within'); }, 0); });
        const hold = (ms) => () => { holdUntil = performance.now() + ms; v = 0; };
        row.addEventListener('touchstart', hold(2500), { passive: true });
        row.addEventListener('touchmove', hold(2500), { passive: true });
        row.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) hold(1500)(); }, { passive: true });
        let rw = innerWidth;
        addEventListener('resize', () => { if (innerWidth !== rw) { rw = innerWidth; rebuild(); } }, { passive: true });
        rebuild();
        if (document.fonts?.ready) document.fonts.ready.then(rebuild);
      }

      const chips = [...root.querySelectorAll('[data-tl-filter]')];
      if (!chips.length || !cards.length) return;
      const ranges = {};
      cards.forEach((c) => {
        const p = c.dataset.tlPhase;
        const a = dUTC(c.dataset.lslFrom);
        const b = dUTC(c.dataset.lslTo);
        const r = ranges[p] || (ranges[p] = { a, b, n: 0 });
        r.n += 1;
        if (a != null && a < r.a) r.a = a;
        if (b != null && b > r.b) r.b = b;
      });
      chips.forEach((ch) => {
        const k = ch.dataset.tlFilter;
        const n = k === 'all' ? cards.length : (ranges[k] ? ranges[k].n : 0);
        if (!n) { ch.hidden = true; return; }
        const b = document.createElement('b');
        b.textContent = n;
        ch.append(' ', b);
      });
      const pick = (k) => {
        chips.forEach((ch) => ch.setAttribute('aria-pressed', String(ch.dataset.tlFilter === k)));
        cards.forEach((c) => { c.hidden = !(k === 'all' || c.dataset.tlPhase === k); });
        const r = k === 'all' ? null : ranges[k];
        datesEl?.querySelectorAll('[data-date]').forEach((d) => {
          const v = dUTC(d.dataset.date);
          d.classList.toggle('is-off', !!r && (v < r.a || v > r.b));
        });
        row.scrollLeft = 0;
        rebuild();
      };
      chips.forEach((ch) => ch.addEventListener('click', () => pick(ch.dataset.tlFilter)));
    });
  };

  /* Sale timeline (ls-sale-timeline): today's tick and the today line. Row states are sale phases. */
  const initTimelines = () => {
    document.querySelectorAll('[data-lsl-tl]:not([data-lsl-ready])').forEach((root) => {
      root.dataset.lslReady = '';
      const start = dUTC(root.dataset.from);
      const end = dUTC(root.dataset.to);
      const rows = root.querySelector('.lsl-tl__rows');
      const now = root.querySelector('[data-tl-now]');
      const paint = () => {
        const local = Date.now() + tzMinutes() * 60000;
        const day = Math.floor(local / DAY) * DAY;
        const frac = (local - day) / DAY;
        root.querySelectorAll('.lsl-tl__tick').forEach((k) => k.classList.toggle('is-today', dUTC(k.dataset.date) === day));
        const track = root.querySelector('.lsl-tl__track');
        if (!now || !track || start == null || end == null || day < start || day > end) { if (now) now.hidden = true; return; }
        const span = Math.round((end - start) / DAY) + 1;
        const tr = track.getBoundingClientRect();
        const rr = rows.getBoundingClientRect();
        if (!tr.width) { now.hidden = true; return; }
        now.style.left = (tr.left - rr.left + tr.width * ((day - start) / DAY + frac) / span).toFixed(1) + 'px';
        now.hidden = false;
      };
      paint();
      setInterval(paint, 60000);
      addEventListener('resize', paint, { passive: true });
    });
  };

  /* Arriving with #section (the homepage planner's links): the browser jumps before the images and rails
     above have their height, so line the section up again until the page settles or the visitor moves */
  const initHash = () => {
    let el = null;
    try { el = location.hash.length > 1 ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null; } catch (e) { el = null; }
    if (!el || !el.closest('.lsl')) return;
    const tgt = el.parentElement && el.parentElement.matches('.ls-sale-section') ? el.parentElement : el;
    let stop = false;
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((t) => addEventListener(t, () => { stop = true; }, { once: true, passive: true }));
    const align = () => {
      if (stop) return;
      /* older browsers reject behavior 'instant' with a TypeError */
      try { tgt.scrollIntoView({ block: 'start', behavior: 'instant' }); } catch (e) { tgt.scrollIntoView(true); }
    };
    align();
    if (document.readyState !== 'complete') addEventListener('load', align, { once: true });
    [300, 800, 1500, 2500, 4000].forEach((t) => setTimeout(align, t));
  };

  const start = () => {
    initKeys();
    spaceNumbers();
    applyPhases();
    initClocks();
    initNav();
    initNavFloat();
    initRcPick();
    initLucky();
    initLaunch();
    initRails();
    initDrawers();
    initBuy();
    initProducts();
    initMeters();
    initSpot();
    initRules();
    initPlanner();
    initTimelines();
    initTimelineCards();
    initDrops();
    initCopy();
    initPeek();
    initHash();
    if (motionOn()) {
      loadMotion().then(([gsap, st]) => {
        G = gsap;
        ST = st;
        root.classList.add('lsl-motion-ready');
        initMotion();
      }).catch(() => root.classList.remove('lsl-motion'));
    }
    setInterval(() => { if (!document.hidden) applyPhases(); }, 30000);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) applyPhases(); });
    document.addEventListener('shopify:section:load', () => { initKeys(); spaceNumbers(); applyPhases(); initRails(); initProducts(); initMeters(); initSpot(); initPlanner(); initTimelines(); initTimelineCards(); initDrops(); initPeek(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
